'use client'

import { createGlobalStyle } from 'styled-components'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import {
  saveQuestionResponse,
  uploadVideoResponse,
  submitAssessment,
  tickQuestionTime,
  pauseQuestionTime,
  initQuestionTime,
} from '../../actions'
import selectors from '../../selectors'
import { 
  trackFullscreenEntered,
  trackFullscreenExited,
  trackViolationDetected,
  trackAssessmentAutoSubmitted,
  trackAssessmentDroppedOff,
  trackRightClick,
} from '../../../../utils/assessmentEvents'
import Completion from './completion'
import { createInflightUploads } from './inflightUploads'
import PluginLiveLogo from 'components/icons/PluginLiveLogo'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { useLocation } from 'react-router-dom'
import studentRequest from 'utils/studentRequest'
import { getUserMediaWithSelectedDevices } from 'utils/mediaDevices'
import { mountHiddenProctorVideo, unmountProctorVideo, watchProctorTrack } from 'utils/proctorVideo'
import useProctoringCollector from 'utils/useProctoringCollector'
import useCameraGuard, { isCameraLive } from 'utils/useCameraGuard'
import { shouldEnforceFullscreen, markMobileInterruption, isMobileInterruptionFullscreenExit } from 'utils/deviceTier'
import Retry from '../../../../components/icons/Retry'
import WhiteRecordIcon from '../../../../components/icons/WhiteRecordIcon'
import StopRecordIcon from '../../../../components/icons/StopRecordIcon'
import DeleteImage from '../../../../components/Images/OBJECTS.png'
import SuccessModal from '../../../../components/SuccessModal'

// Constants
const MAX_TAB_SWITCH_WARNINGS = 4
const MAX_VIDEO_RECORDING_TIME = 120
const MIN_VIDEO_RECORDING_TIME = 60

const MarkdownGlobalStyle = createGlobalStyle`
  .markdown-body, .markdown-body * {
    color: #374151 !important;
  }
`
const FullscreenModalOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`

const FullscreenModalContent = styled.div`
  max-width: 500px;
  width: 90%;
  padding: 30px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
`

const FullscreenTitle = styled.h2`
  color: #4318ff;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
`

const FullscreenDescription = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
  color: #374151;
  font-size: 14px;

  strong {
    font-weight: 600;
    color: #111827;
  }

  ul {
    text-align: left;
    margin: 10px 0;
    padding-left: 20px;

    li {
      margin: 8px 0;
      color: #4b5563;
    }
  }
`

const PrimaryButton = styled.button`
  background-color: #4318ff;
  color: white;
  font-weight: bold;
  height: 44px;
  border-radius: 4px;
  border: none;
  padding: 0 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  &:hover {
    background-color: #3512cc;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(67, 24, 255, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const InfoContainer = styled.div`
  background-color: #f9fafb;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
`

const InfoIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: #4318ff;
  color: white;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
`

const InfoText = styled.p`
  font-weight: 400;
  font-style: italic;
  font-size: 15px;
  line-height: 109%;
  letter-spacing: 0%;
  color: #111827;
  margin: 0;

  strong {
    font-weight: 600;
    font-style: italic;
    line-height: 100%;
    letter-spacing: 0%;
  }
`

// Enhanced Fullscreen Assessment Container
const FullscreenAssessmentContainer = styled.div`
  position: ${props => (props.isFullscreen ? 'fixed' : 'relative')};
  top: ${props => (props.isFullscreen ? '0' : 'auto')};
  left: ${props => (props.isFullscreen ? '0' : 'auto')};
  width: ${props => (props.isFullscreen ? '100vw' : '100%')};
  height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  min-height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  z-index: ${props => (props.isFullscreen ? '99999' : 'auto')};
  display: flex;
  background: #f9fafb;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
`

const SIDEBAR_EXPANDED_WIDTH = 300
const SIDEBAR_COLLAPSED_WIDTH = 60

const SidebarNav = styled.div`
  width: ${props => (props.isExpanded ? `${SIDEBAR_EXPANDED_WIDTH}px` : `${SIDEBAR_COLLAPSED_WIDTH}px`)};
  min-width: ${props => (props.isExpanded ? `${SIDEBAR_EXPANDED_WIDTH}px` : `${SIDEBAR_COLLAPSED_WIDTH}px`)};
  max-width: ${props => (props.isExpanded ? `${SIDEBAR_EXPANDED_WIDTH}px` : `${SIDEBAR_COLLAPSED_WIDTH}px`)};
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  overflow: hidden;
  flex-shrink: 0;
`

const SidebarHeader = styled.div`
  padding: ${props => (props.isExpanded ? '20px 16px' : '20px 0')};
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  min-height: 64px;
  flex-shrink: 0;

  ${props =>
    props.isExpanded &&
    `
    justify-content: space-between;
  `}
`

const SidebarTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #374151;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  display: ${props => (props.isExpanded ? 'block' : 'none')};
`

const ToggleButton = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => (props.isExpanded ? '16px 12px' : '16px 6px')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`

const SectionTitle = styled.h4`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 12px 0;
  padding: 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  white-space: nowrap;
`

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const QuestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => (props.isExpanded ? '10px 8px' : '12px 6px')};
  border-radius: 6px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-height: ${props => (props.isExpanded ? '40px' : '48px')};
  position: relative;
  margin-bottom: 2px;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};

  &:hover {
    background: ${props => (props.disabled ? '#ffffff' : '#f8fafc')};
    border-color: ${props => (props.disabled ? '#e5e7eb' : '#d1d5db')};
    transform: ${props => (props.disabled ? 'none' : 'translateX(2px)')};
    box-shadow: ${props => (props.disabled ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)')};
  }

  ${props =>
    props.isExpanded
      ? ''
      : `
    justify-content: center;
  `}
`

const QuestionNumber = styled.div`
  width: ${props => (props.isExpanded ? '28px' : '36px')};
  height: ${props => (props.isExpanded ? '28px' : '36px')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${props => (props.isExpanded ? '12px' : '13px')};
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;

  ${props => {
    switch (props.status) {
      case 'current':
        return `
          background: #3b82f6;
          color: white;
          box-shadow: 0 0 0 3px #eff6ff;
          border: 2px solid #3b82f6;
        `
      case 'attempted':
        return `
          background: #10b981;
          color: white;
          border: 2px solid #10b981;
        `
      case 'skipped':
        return `
          background: #6b7280;
          color: white;
          border: 2px solid #6b7280;
        `
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        `
    }
  }}
`
// Add this styled component after QuestionNumber (around line 240):
const QuestionText = styled.div`
  margin-left: ${props => (props.isExpanded ? '8px' : '0')};
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex: 1;
  line-height: 1.3;
  display: flex;
  align-items: center;
  ${props =>
    props.isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
  `}
  ${props =>
    !props.isExpanded &&
    `
    display: none;
  `}
  & .markdown-body > * {
    margin: 0 !important;
  }
`
const QuestionStatus = styled.div`
  margin-left: auto;
  font-size: 12px;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex-shrink: 0;

  ${props => {
    switch (props.status) {
      case 'attempted':
        return 'color: #10b981;'
      case 'skipped':
        return 'color: #6b7280;'
      case 'current':
        return 'color: #3b82f6;'
      default:
        return 'color: #9ca3af;'
    }
  }}
`

// Content Area
const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-y: auto;
  position: relative;
  background: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`

const AssessmentContent = styled.div`
  flex: 1;
  padding: 20px 24px 24px 24px;
  display: flex;
  flex-direction: column;
`

const AssessmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;

  svg {
    height: 56px;
    width: auto;
  }

  img {
    height: 56px;
    width: auto;
    object-fit: contain;
  }

  > * {
    display: flex;
    align-items: center;
    height: 56px;
    width: auto;
  }
`

const AssessmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
`

const VerticalSeparator = styled.div`
  width: 1px;
  height: 28px;
  background: #d1d5db;
  margin: 0 4px;
  flex-shrink: 0;
`

const AssessmentTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
`

const AssessmentTimer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4318ff;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const AssessmentSubtitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 20px 0;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SubtitleText = styled.h2`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
`

// Status section
const StatusSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 60px;
`

const StatusNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color || '#3b82f6'};
  margin-bottom: 4px;
`

const StatusLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ProgressContainer = styled.div`
  flex: 1;
  margin: 0 20px;
`

const ProgressText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 6px;
  text-align: center;
`

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: #3b82f6;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`

const StatusSeparator = styled.div`
  width: 1.5px;
  height: 32px;
  background: linear-gradient(to bottom, #e5e7eb 60%, #d1d5db 100%);
  margin: 0 12px;
  border-radius: 2px;
  align-self: center;
`

// Question Container
const QuestionContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const QuestionContent = styled.div`
  margin-bottom: 24px;
  
  & .markdown-body {
    font-size: 16px;
    line-height: 1.6;
    color: #374151;
  }
  
  & .markdown-body > * {
    margin-bottom: 16px !important;
  }
  
  & .markdown-body > *:last-child {
    margin-bottom: 0 !important;
  }
`

const QuestionTypeTag = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.type) {
      case 'MCQ Question':
        return `
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #dbeafe;
        `
      case 'Subjective Question':
        return `
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
        `
      case 'Video Response':
        return `
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        `
      case 'Coding Question':
        return `
          background: #faf5ff;
          color: #7c3aed;
          border: 1px solid #e9d5ff;
        `
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        `
    }
  }}
`


// MCQ Options
const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: #fff;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  }

  ${props =>
    props.checked &&
    `
    border-color: #3b82f6;
    background: #eff6ff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  `}
`

const RadioInput = styled.input`
  accent-color: #3b82f6;
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
`

const OptionText = styled.div`
  flex: 1;
  line-height: 1.5;
  color: #374151;
  
  & .markdown-body > * {
    margin: 0 !important;
  }
`

// Subjective Response
const SubjectiveContainer = styled.div`
  margin-top: 20px;
`

const SubjectiveLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`

const SubjectiveTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  max-height: 400px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const CharacterCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
`

// Video Response
const VideoContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`

const VideoLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`

const VideoInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  background: #F0F4FF;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  gap: 8px;
`

const VideoInfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #4318FF;
  color: white;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
`

const VideoInfoText = styled.span`
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
`

const VideoRecordingArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
`

const VideoPlayer = styled.div`
  width: 100%;
  max-width: 640px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background-color: #000;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  aspect-ratio: 16/9;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 12px;
  }
`

const VideoTimerBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: #ffffff;
  color: #000000;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  z-index: 10;
`

const VideoTimerDot = styled.div`
  width: 10px;
  height: 10px;
  background-color: #FF3B30;
  border-radius: 50%;
  margin-right: 8px;
`

const VideoStopButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => (props.enabled ? '#FF3538' : '#FF8385')};
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => (props.enabled ? 'pointer' : 'not-allowed')};
  transition: all 0.2s ease;
  width: 180px;
  height: 40px;
  svg {
    margin-left: 6px;
    margin-right: 0;
  }
  &:hover {
    background-color: ${props => (props.enabled ? '#e02d30' : '#FF8385')};
  }
`

const VideoRecordButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #4318ff;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: 160px;
  height: 40px;
  svg {
    margin-left: 8px;
    margin-right: 0;
    width: 20px;
    height: 20px;
  }
  &:hover {
    background-color: #3512cc;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`

const VideoReRecordButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FF7300;
  color: #FFFFFF;
  border: none;
  border-radius: 30px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  width: 149px;
  height: 40px;
`

const VideoAttemptsText = styled.div`
  margin-top: 16px;
  font-size: 16px;
`

const VideoDeleteModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const VideoDeleteModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  z-index: 10000;
`

const VideoDeleteModalImg = styled.div`
  margin-bottom: 16px;
  img {
    width: 120px;
    height: auto;
  }
`

const VideoDeleteModalText = styled.p`
  font-size: 16px;
  color: #374151;
  margin-bottom: 24px;
`

const VideoDeleteModalButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`

// Keep RecordButton for backward compatibility with navigation disable logic
const RecordButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: #3b82f6;
  color: white;
  &:hover {
    background: #2563eb;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`

// Navigation Container - sticky within ContentArea, not fixed to viewport
const StickyNavigationContainer = styled.div`
  position: sticky;
  bottom: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 50;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const NavigationButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 100px;

  ${props => {
    if (props.variant === 'primary') {
      return `
        background: #3b82f6;
        color: white;
        &:hover:not(:disabled) {
          background: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
      `
    } else if (props.variant === 'secondary') {
      return `
        background: #ffffff;
        color: #6b7280;
        border: 1px solid #d1d5db;
        &:hover:not(:disabled) {
          background: #f9fafb;
          border-color: #9ca3af;
        }
      `
    } else if (props.variant === 'danger') {
      return `
        background: #dc2626;
        color: white;
        &:hover:not(:disabled) {
          background: #b91c1c;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }
      `
    }
  }}

  &:disabled {
    background: #f3f4f6;
    color: #d1d5db;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const FullscreenEntryModal = ({ onAccept }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Enable Full-Page Assessment Mode</FullscreenTitle>
        <FullscreenDescription>
          This assessment will open in full-page mode for optimal focus and
          proctoring purposes. The entire screen will be dedicated to the
          assessment.
        </FullscreenDescription>

        <FullscreenDescription>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={{ marginLeft: '27px' }}>
              <strong>Important:</strong> During this assessment:
            </span>
            <ul style={{ margin: '10px 0' }}>
              <li>The assessment will take over the entire screen</li>
              <li>Navigation elements will be hidden</li>
              <li>Switching tabs is not allowed (3 warnings; the fourth violation auto-submits)</li>
              <li>Browser navigation is disabled</li>
              <li>Key combinations like Alt+Tab are blocked</li>
            </ul>
            Violations may result in automatic submission of your assessment.
          </div>
        </FullscreenDescription>

        <PrimaryButton onClick={onAccept}>
          Accept & Start Assessment
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

const FullscreenReentryModal = ({ onReturn, warningCount, lite = false }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Return to Assessment</FullscreenTitle>
        <FullscreenDescription>
          {lite
            ? 'You left the assessment. For security and proctoring purposes, please stay on this screen until you finish.'
            : 'You have exited the assessment mode. For security and proctoring purposes, you must return to full-page assessment mode to continue.'}
        </FullscreenDescription>

        <InfoContainer>
          <InfoIcon>!</InfoIcon>
          <InfoText>
            <strong>Warning {warningCount}/3:</strong> A fourth violation will result in automatic submission of your assessment
            and will be flagged as suspicious.
          </InfoText>
        </InfoContainer>

        <PrimaryButton onClick={onReturn}>
          {lite ? 'Resume Assessment' : 'Return to Assessment'}
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

const ViolationModal = ({ onSubmit }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Assessment will be auto-submitted</FullscreenTitle>
        <FullscreenDescription>
          You&apos;ve violated the rules more than 3 times, so your assessment will be auto-submitted.
        </FullscreenDescription>

        <PrimaryButton onClick={onSubmit}>
          <span style={{ fontWeight: 'bold' }}>Understood</span>
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

const { ENVIRONMENT } = process.env;
const disableCopyPaste = ['dev', 'development', 'staging', 'prod', 'production'].includes((ENVIRONMENT || '').toLowerCase());

const RoleBasedAssessment = (props) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [questions, setQuestions] = useState([])
  const [responses, setResponses] = useState({})
  const [isCompleted, setIsCompleted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [timeLeft, setTimeLeft] = useState(null) // Set dynamically from API
  const [assessmentStartTime, setAssessmentStartTime] = useState(null)
  // Refs mirror the two timing values so they survive the parent's prop churn
  // and stay readable from handleSubmit without widening its dependency list.
  const assessmentStartedAtRef = useRef(null)
  const totalDurationRef = useRef(null)
  const dispatch = useDispatch()

  // Video recording states
  const [videoRecording, setVideoRecording] = useState(false)
  const [recordedVideos, setRecordedVideos] = useState({})
  const [subjectiveResponses, setSubjectiveResponses] = useState({})
  const [codingResponses, setCodingResponses] = useState({})
  // { [questionId]: { state: 'saving' | 'saved' | 'error', message?: string } }
  const [codingSaveStatus, setCodingSaveStatus] = useState({})
  const [selectedLanguage, setSelectedLanguage] = useState({})
  const [codeRunning, setCodeRunning] = useState({})
  const [codeOutput, setCodeOutput] = useState({})
  const [videoStreams, setVideoStreams] = useState({})
  const [videoRecorders, setVideoRecorders] = useState({})
  const [videoBlobs, setVideoBlobs] = useState({})
  const videoRefs = useRef({})
  const videoChunks = useRef({})
  // iPhone tab/app-switch recovery: iOS suspends camera capture when the page is
  // backgrounded, which kills an active video-response recording. These refs let
  // the visibility handler (bound once) see the live recording and discard the
  // orphaned/partial take so the candidate can re-record.
  const videoRecordingRef = useRef(false)      // active questionId while recording, else false
  const activeVideoRecorderRef = useRef(null)
  const interruptedVideoRef = useRef(false)

  // Communication-style video UI states
  const [videoRemainingAttempts, setVideoRemainingAttempts] = useState({})
  const [videoRecordingTimer, setVideoRecordingTimer] = useState(0)
  const [videoMinReached, setVideoMinReached] = useState(false)
  const [videoDeleteModalQuestion, setVideoDeleteModalQuestion] = useState(null)
  const [videoShowSuccessModal, setVideoShowSuccessModal] = useState(false)
  const videoTimerRef = useRef(null)
  
  const location = useLocation()
  const currentAssessment = useSelector(state => state.assessment.currentAssessment)
  const assessmentData = location.state?.assessmentData
  const [submittedTime, setSubmittedTime] = useState(0)
  const [startTime, setStartTime] = useState(Date.now())
const proctoringEnabled = currentAssessment?.allowProctoring !== false
  // Fullscreen lockdown is only enforceable on devices that support element
  // fullscreen (desktop / macOS Safari / iPad). iPhone Safari cannot, so there
  // we run "lite" mode: snapshots + tab/app-switch detection stay ON, but the
  // fullscreen entry gate, re-entry-to-fullscreen and fullscreen-exit
  // violations are skipped.
  const enforceFullscreen = proctoringEnabled && shouldEnforceFullscreen()
const [showFullscreenModal, setShowFullscreenModal] = useState(enforceFullscreen)
  const [showReentryModal, setShowReentryModal] = useState(false)
  const [showViolationModal, setShowViolationModal] = useState(false)
  const [violationType, setViolationType] = useState('tab-switch')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isAssessmentFullscreen, setIsAssessmentFullscreen] = useState(false)
  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  const [isAssessmentActive, setIsAssessmentActive] = useState(!enforceFullscreen)  // Auto-activate when there is no fullscreen gate to pass (proctoring off, or lite mode)
  const [proctoringReady, setProctoringReady] = useState(false)

  // Add refs
  const assessmentActiveRef = useRef(isAssessmentActive)
  const completedRef = useRef(isCompleted)
  const handleSubmitRef = useRef(handleSubmit)
  const isAutoSubmittedRef = useRef(false)
  const violationSubmissionStartedRef = useRef(false)
  const violationSubmitTimerRef = useRef(null)
  const submitAfterViolation = () => {
    if (violationSubmissionStartedRef.current) return
    violationSubmissionStartedRef.current = true
    if (violationSubmitTimerRef.current) clearTimeout(violationSubmitTimerRef.current)
    isAutoSubmittedRef.current = true
    handleSubmitRef.current()
  }

  // Mirrors of state read inside handleSubmit. handleSubmit is a useCallback
  // with closure-captured `responses` / `videoBlobs`; on auto-submit (timer
  // expiry) the in-flight recorder's onstop updates state mid-execution, but
  // the closure can't see it. Reading through these refs sidesteps the stale
  // snapshot so the just-uploaded video key is included in the payload.
  const responsesRef = useRef({})
  const videoBlobsRef = useRef({})

  // Tracks HTTP upload promises by questionId. saveVideoResponse hands its
  // dispatch promise to track(); handleSubmit calls drain() before
  // stopAllMediaStreams() so the server-side StudentAnswer row written by
  // /students/assessments/uploadVideo is guaranteed to exist before the
  // cron starts scoring. Matches Communicationassmt's inflight pattern.
  const inflightVideoUploadsRef = useRef(createInflightUploads())

const [captureCounter, setCaptureCounter] = useState(0)
const captureRef = useRef(null)
const canvasRef = useRef(null)
const captureInterval = useRef(null)
const proctoringActive = useRef(false)
const capturingInProgress = useRef(false)
// Report-only gaze collector (MediaPipe eye/head tracking) on the shared proctor
// video, scoped per question. Mirrors aptitude/interview.
const proctoringCollector = useProctoringCollector()
const proctoringCollectorStarted = useRef(false)
const snapshotBurstUntilRef = useRef(0)
// Camera guard: if the camera turns off mid-assessment, silently try to turn
// it back on in the background — no popup, no pause.
const { cameraLost } = useCameraGuard({
  enabled: proctoringEnabled && !!assessmentStartTime && !isCompleted,
  getStream: () => (captureRef.current && captureRef.current.srcObject) || null,
})


// Fullscreen change detection (only when fullscreen lockdown is enforced)
useEffect(() => {
    if (!enforceFullscreen) return

    const handleFullscreenChange = async () => {
      const isCurrentlyFullscreen = Boolean(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      )

      setIsFullscreen(isCurrentlyFullscreen)
      setIsAssessmentFullscreen(isCurrentlyFullscreen)

      if (!isCurrentlyFullscreen && isMobileInterruptionFullscreenExit()) return

      // CRITICAL FIX: Check refs FIRST before any state checks
      // If completed, don't do anything - just exit silently
      if (completedRef.current) {
        console.log('🔍 [Fullscreen] Assessment completed, ignoring fullscreen exit')
        return
      }

      // FIXED: Don't track violations if assessment is completed or being submitted
      if (!isCompleted && isAssessmentActive && !isCurrentlyFullscreen) {
        // Report-only proctoring: fullscreen exit (separate from the warning flow).
        if (proctoringCollectorStarted.current) {
          proctoringCollector.recordEvent('fullscreen_exit', { severity: 'medium', confidence: 1 })
        }
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('fullscreen-exit')
            setIsAssessmentActive(false)
            trackAssessmentAutoSubmitted(
              currentAssessment?.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              currentAssessment?.type,
              currentAssessment?.isCorporate,
              currentAssessment?.entityName
            )
            isAutoSubmittedRef.current = true
            if (currentAssessment?.assessment_assigned_id) {
              studentRequest.post('/students/markAutoSubmitted', {
                assessment_assigned_id: currentAssessment.assessment_assigned_id,
                reason: 'fullscreen-exit-violation',
              }).catch(() => {})
            }
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackFullscreenExited(
              currentAssessment?.assessment_assigned_id,
              currentAssessment?.type,
              newCount,
              currentAssessment?.isCorporate,
              currentAssessment?.entityName
            )
          }
          return newCount
        })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [enforceFullscreen, isAssessmentActive, currentAssessment])
  // Add right-click detection
  useEffect(() => {
    if (!disableCopyPaste) return;
    const handleRightClick = (e) => {
      trackRightClick(
        currentAssessment?.assessment_assigned_id,
        currentAssessment?.type,
        currentAssessment?.isCorporate,
        currentAssessment?.entityName
      )
    }
    document.addEventListener('contextmenu', handleRightClick)
    return () => {
      document.removeEventListener('contextmenu', handleRightClick)
    }
  }, [currentAssessment])

  // Scope the gaze collector to the visible question for the read-from-device
  // fusion. No-op until the collector has started. Stops it on completion.
  useEffect(() => {
    if (!proctoringCollectorStarted.current) return
    if (isCompleted) {
      try { proctoringCollector.stop() } catch (_) {}
      return
    }
    const q = questions?.[currentQuestionIndex]
    if (q?.id) proctoringCollector.setActiveQuestion(currentQuestionIndex, q.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, isCompleted])

  // Add visibility change handler
  useEffect(() => {
    assessmentActiveRef.current = isAssessmentActive
    completedRef.current = isCompleted
    handleSubmitRef.current = handleSubmit
  }, [isAssessmentActive, isCompleted, handleSubmit])

  // Keep refs in sync with state for handleSubmit's stale-closure dodge.
  useEffect(() => { responsesRef.current = responses }, [responses])
  useEffect(() => { videoBlobsRef.current = videoBlobs }, [videoBlobs])

  // Prevent accidental exit during active assessment (reload, back button, tab close)
  useEffect(() => {
    if (!isAssessmentActive || isCompleted) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAssessmentActive, isCompleted])

// Tab switch detection (only when proctoring is enabled)
useEffect(() => {
    if (!proctoringEnabled) return

    const handleVisibilityChange = () => {
      // CRITICAL FIX: Check refs FIRST
      if (completedRef.current) {
        console.log('🔍 [Visibility] Assessment completed, ignoring tab switch')
        return
      }

      if (
        document.visibilityState === 'hidden' &&
        assessmentActiveRef.current &&
        !isCompleted
      ) {
        const isMobileInterruption = markMobileInterruption()
        // Report-only proctoring: tab/app switch (same-device lookup gaze can't see).
        if (proctoringCollectorStarted.current) {
          proctoringCollector.recordEvent('tab_hidden', {
            severity: 'high',
            confidence: 1,
          })
        }
        // If a video-response recording is live, iOS is about to suspend the
        // capture and kill it. Flag it so onstop (whenever it fires) discards
        // the partial take instead of saving/uploading a corrupt clip.
        if (videoRecordingRef.current) {
          interruptedVideoRef.current = true
        }
        if (isMobileInterruption) return
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('tab-switch')
            setIsAssessmentActive(false)
            trackViolationDetected(
              currentAssessment?.assessment_assigned_id,
              currentAssessment?.type,
              newCount,
              'tab-switch',
              currentAssessment?.isCorporate,
              currentAssessment?.entityName
            )
            trackAssessmentAutoSubmitted(
              currentAssessment?.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              currentAssessment?.type,
              currentAssessment?.isCorporate,
              currentAssessment?.entityName
            )
            isAutoSubmittedRef.current = true
            if (currentAssessment?.assessment_assigned_id) {
              studentRequest.post('/students/markAutoSubmitted', {
                assessment_assigned_id: currentAssessment.assessment_assigned_id,
                reason: 'tab-switch-violation',
              }).catch(() => {})
            }
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackViolationDetected(
              currentAssessment?.assessment_assigned_id,
              currentAssessment?.type,
              newCount,
              'tab-switch',
              currentAssessment?.isCorporate,
              currentAssessment?.entityName
            )
          }
          return newCount
        })
      } else if (
        document.visibilityState === 'visible' &&
        !shouldEnforceFullscreen() &&
        assessmentActiveRef.current &&
        !isCompleted
      ) {
        // iPhone: a video-response recording was active when the app went to the
        // background. iOS killed the capture and it can't resume, so stop the
        // orphaned recorder — its onstop (flagged interrupted) discards the
        // partial take and resets to the ready-to-record state. If iOS already
        // tore the recorder down without firing onstop, reset manually.
        if (videoRecordingRef.current) {
          const qId = videoRecordingRef.current
          interruptedVideoRef.current = true
          try {
            if (activeVideoRecorderRef.current && activeVideoRecorderRef.current.state !== 'inactive') {
              activeVideoRecorderRef.current.stop()
            } else {
              videoRecordingRef.current = false
              activeVideoRecorderRef.current = null
              if (qId) videoChunks.current[qId] = []
              setVideoRecording(false)
              setVideoRecordingTimer(0)
              setVideoMinReached(false)
            }
          } catch (_) {
            videoRecordingRef.current = false
            activeVideoRecorderRef.current = null
            setVideoRecording(false)
          }
          return
        }
        // iPhone only: iOS suspends the proctoring camera when the tab/app is
        // backgrounded, so on return the stream is dead (frozen/black frames).
        // Reset the capture ref so the next snapshot re-acquires getUserMedia,
        // and kick one immediately so proctoring resumes instead of staying
        // stopped. Desktop/iPad enforce fullscreen and are unaffected.
        try {
          if (captureRef.current && captureRef.current.srcObject) {
            captureRef.current.srcObject.getTracks().forEach(t => { try { t.stop() } catch (_) {} })
            captureRef.current.srcObject = null
          }
        } catch (_) {}
        captureRef.current = null
        try {
          if (window.proctorStreamTracks) {
            window.proctorStreamTracks.forEach(t => { try { t.stop() } catch (_) {} })
            window.proctorStreamTracks = []
          }
        } catch (_) {}
        if (proctoringActive.current) {
          if (!captureInterval.current) setupProctoringInterval()
          setTimeout(() => {
            captureImage()
              .then(b => { if (b) uploadProctorImage(b) })
              .catch(() => {})
          }, 800)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [proctoringEnabled, currentAssessment])

  // Fullscreen body class management (only when proctoring is enabled)
  useEffect(() => {
    if (!proctoringEnabled) return

    if (isAssessmentFullscreen) {
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }
    } else {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }

    return () => {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }
  }, [proctoringEnabled, isAssessmentFullscreen])

  // Date-based timer: pin endTime once and compute remaining each tick.
  // Using [] deps + refs avoids the drift-prone "deps=[timeLeft]" pattern that
  // tore down and recreated the interval every second.
  const endTimeRef = useRef(null)
  const isCompletedRef = useRef(isCompleted)
  const handleSubmitRefTimer = useRef(handleSubmit)
  const submittedRef = useRef(false)
  useEffect(() => { isCompletedRef.current = isCompleted }, [isCompleted])
  useEffect(() => { handleSubmitRefTimer.current = handleSubmit }, [handleSubmit])

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || isCompleted) return
    if (endTimeRef.current === null) {
      endTimeRef.current = Date.now() + timeLeft * 1000
    }
  }, [timeLeft, isCompleted])

  useEffect(() => {
    const timer = setInterval(() => {
      if (!endTimeRef.current || isCompletedRef.current) return
      const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000))
      if (remaining <= 0) {
        if (!submittedRef.current) {
          submittedRef.current = true
          setTimeLeft(0)
          handleSubmitRefTimer.current && handleSubmitRefTimer.current()
        }
        return
      }
      setTimeLeft(remaining)
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Format time display
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Get questions from props
  useEffect(() => {
    console.log('🔍 Assessment component received props:', props)
    console.log('🔍 Questions data:', props.questions)
    
    if (props.questions && props.questions.data && props.questions.data.sections) {
      console.log('📊 Processing sections:', props.questions.data.sections)
      
      const allQuestions = []
      let questionIndex = 0
      
      // Flatten questions from all sections
      props.questions.data.sections.forEach(section => {
        console.log(`📝 Processing section: ${section.section_name} with ${section.questions?.length || 0} questions`)
        
        if (section.questions && section.questions.length > 0) {
          section.questions.forEach(question => {
            allQuestions.push({
              ...question,
              section_name: section.section_name,
              index: questionIndex++
            })
          })
        }
      })
      
      console.log('✅ Total questions processed:', allQuestions.length)
      setQuestions(allQuestions)

      // Set duration from API (dynamic based on question count) or fallback to 30 min.
      // Pinned once: the parent re-creates the `questions` prop object on every
      // render, so this effect re-runs on each answer saved. Re-seeding timeLeft
      // there would bounce the visible countdown back to the full duration.
      if (totalDurationRef.current === null) {
        const durationMinutes = props.questions.data.duration_minutes || 30;
        totalDurationRef.current = durationMinutes * 60;
        setTimeLeft(durationMinutes * 60);
        console.log(`⏱️ Assessment duration: ${durationMinutes} minutes (${durationMinutes * 60}s)`)
      }

      // Pre-fill coding responses with starter code and auto-set language.
      // Filter to languages the backend can score so a Python-only question
      // doesn't get pinned to 'javascript' just because that's first in the list.
      const EVALUATED = ['python', 'javascript', 'typescript', 'java', 'cpp', 'c']
      const initialCodingResponses = {}
      const initialLanguages = {}
      allQuestions.forEach(q => {
        if (q.section_name === 'Coding Question' && q.coding_metadata) {
          const declared = (q.coding_metadata.languages || []).filter(l => EVALUATED.includes(l))
          const lang = declared[0]
            || (EVALUATED.includes(q.coding_metadata.language) ? q.coding_metadata.language : null)
            || 'python'
          initialLanguages[q.id] = lang
          const starterCode = typeof q.coding_metadata.starter_code === 'string'
            ? q.coding_metadata.starter_code
            : (q.coding_metadata.starter_code && q.coding_metadata.starter_code[lang]) || ''
          if (starterCode) {
            initialCodingResponses[q.id] = starterCode
          }
        }
      })
      setCodingResponses(prev => ({ ...initialCodingResponses, ...prev }))
      setSelectedLanguage(prev => ({ ...initialLanguages, ...prev }))

      setLoading(false)
      // Pinned once, for the same reason as the duration above. Left unguarded
      // this reset on every answer saved, which zeroed the reported time taken
      // and made the camera guard flap off/on.
      if (assessmentStartedAtRef.current === null) {
        assessmentStartedAtRef.current = Date.now()
        setAssessmentStartTime(assessmentStartedAtRef.current)
      }
    } else {
      console.log('❌ No questions data found in props')
      setLoading(false)
    }
  }, [props.questions])


// Resolve (assessmentAssignedId, studentId) used by every proctoring call.
const resolveProctorIds = () => {
  const assessmentAssignedId =
    currentAssessment?.assessment_assigned_id ||
    props.assessment?.assessment_assigned_id ||
    location?.state?.assessmentData?.assessment_assigned_id ||
    location?.state?.assessment_assigned_id
  const otpAssignedId =
    (typeof sessionStorage !== 'undefined' &&
      (sessionStorage.getItem('assessment_invite_assigned_id') ||
        sessionStorage.getItem('ai_interview_assessment_assigned_id'))) || null
  const studentId =
    studentData?.id || studentData?.studentId || studentData?.student_id ||
    otpAssignedId || assessmentAssignedId
  return { assessmentAssignedId, studentId }
}

// Start the MediaPipe gaze collector once on the live proctor video, scoped per
// question. Report-only; no-op without an id. Mirrors aptitude/interview.
const startCollectorIfReady = () => {
  if (proctoringCollectorStarted.current) return
  const { assessmentAssignedId, studentId } = resolveProctorIds()
  if (!assessmentAssignedId || !captureRef.current) return
  proctoringCollectorStarted.current = true
  proctoringCollector.start({
    videoEl: captureRef.current,
    assessmentAssignedId,
    studentId,
    sessionId: null,
    // Role_Based mixes typed/clicked sections (MCQ, Subjective, Coding) with a
    // spoken one (Video Response) — candidates naturally look at the keyboard
    // while typing, so gaze-away/head-turned proctoring is disabled across the
    // whole assessment; reading-from-a-device detection stays on.
    disableGazeEvents: true,
    onSuspicion: () => { snapshotBurstUntilRef.current = Date.now() + 30000 },
  })
  try {
    if (window.screen && window.screen.isExtended === true) {
      proctoringCollector.recordEvent('second_display', { severity: 'high', confidence: 0.8 })
    }
  } catch (_) { /* unsupported → unflagged */ }
  const q = questions?.[currentQuestionIndex]
  if (q?.id) proctoringCollector.setActiveQuestion(currentQuestionIndex, q.id)
}

const setupProctoringInterval = () => {
  if (captureInterval.current) {
    clearTimeout(captureInterval.current)
  }
  const SNAP_MS = 20000
  const SNAP_BURST_MS = 5000
  const runOnce = async () => {
    if (proctoringActive.current && !capturingInProgress.current) {
      try {
        const imageBlob = await captureImage()
        if (imageBlob) {
          await uploadProctorImage(imageBlob)
        }
      } catch (error) {
        console.error('Error in proctoring interval:', error)
      }
    }
    const inBurst = Date.now() < snapshotBurstUntilRef.current
    captureInterval.current = setTimeout(runOnce, inBurst ? SNAP_BURST_MS : SNAP_MS)
  }
  captureInterval.current = setTimeout(runOnce, 20000)
}

const captureImage = async () => {
  if (capturingInProgress.current) return null
  capturingInProgress.current = true
  
  try {
    const safetyClearTimeout = setTimeout(() => {
      if (capturingInProgress.current) capturingInProgress.current = false
    }, 15000)

    if (!captureRef.current) {
      try {
        // video+audio so the SAME stream can also feed the video-response
        // recorder (one shared camera — never a 2nd stream).
        const proctorStream = await getUserMediaWithSelectedDevices({
          video: { width: 640, height: 480 },
          audio: true,
        })
        captureRef.current = document.createElement('video')
        mountHiddenProctorVideo(captureRef.current)
        watchProctorTrack(proctorStream, () => {
          unmountProctorVideo(captureRef.current)
          captureRef.current = null
          window.proctorStreamTracks = []
        })
        captureRef.current.srcObject = proctorStream
        captureRef.current.autoplay = true
        captureRef.current.muted = true
        // iOS Safari requires playsInline or hidden muted videos won't play
        // → MediaPipe gaze silently no-ops. Keep in sync with the
        // interview + other modules' proctor videos.
        captureRef.current.playsInline = true
        await new Promise(resolve => {
          captureRef.current.onloadedmetadata = () => {
            captureRef.current.play().then(resolve).catch(resolve)
          }
          setTimeout(resolve, 1000)
        })
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas')
          canvasRef.current.width = 640
          canvasRef.current.height = 480
        }
        if (!window.proctorStreamTracks) window.proctorStreamTracks = []
        proctorStream.getTracks().forEach(track => {
          window.proctorStreamTracks.push(track)
        })
        proctoringActive.current = true
        startCollectorIfReady()
      } catch (err) {
        console.error('Failed to create proctoring stream:', err)
        clearTimeout(safetyClearTimeout)
        capturingInProgress.current = false
        return null
      }
    }

    try {
      const context = canvasRef.current.getContext('2d')
      context.drawImage(
        captureRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      )
      const blob = await new Promise(resolve => {
        canvasRef.current.toBlob(resolve, 'image/jpeg', 0.8)
      })
      clearTimeout(safetyClearTimeout)
      if (blob) {
        setCaptureCounter(prev => prev + 1)
        capturingInProgress.current = false
        return blob
      }
    } catch (err) {
      console.error('Error in standard capture mode:', err)
    }
    clearTimeout(safetyClearTimeout)
    capturingInProgress.current = false
    return null
  } catch (error) {
    console.error('Overall capture error:', error)
    capturingInProgress.current = false
    return null
  }
}

const uploadProctorImage = async imageBlob => {
  if (!imageBlob) return
  
  try {
    // FIXED: Get assessment_assigned_id with comprehensive fallbacks
    const assessmentAssignedId = 
      currentAssessment?.assessment_assigned_id ||
      props.assessment?.assessment_assigned_id ||
      props.currentAssessment?.assessment_assigned_id ||
      location?.state?.assessmentData?.assessment_assigned_id ||
      location?.state?.assessment_assigned_id ||
      assessmentData?.assessment_assigned_id

    // FIXED: Get student ID using Redux store (same as aptitude assessment).
    // OTP/email-invite flow has no studentData → fall back to the invite assigned
    // id, then the assessmentAssignedId (mirrors AIInterview).
    const otpAssignedId =
      (typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('assessment_invite_assigned_id')) || null
    const studentId =
      studentData?.id ||
      studentData?.studentId ||
      studentData?.student_id ||
      props.studentDetails?.id ||
      props.studentDetails?.studentId ||
      props.studentDetails?.student_id ||
      currentAssessment?.studentId ||
      currentAssessment?.student_id ||
      otpAssignedId ||
      assessmentAssignedId

    if (!assessmentAssignedId || !studentId) {
      console.error('❌ [PROCTORING] Missing required IDs for upload:', {
        assessmentAssignedId,
        studentId,
        studentData
      })
      return
    }

    console.log('🔍 [PROCTORING] Uploading image with IDs:', {
      assessmentAssignedId,
      studentId
    })

    const base64Image = await blobToBase64(imageBlob)
    const formData = new FormData()
    const byteCharacters = atob(base64Image)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const imageFile = new Blob([byteArray], { type: 'image/jpeg' })
    
    // Text fields BEFORE the file — fastify-multipart on the server only
    // sees fields parsed before the file boundary in the multipart stream
    // when read via req.file(). Order matters on PROD where the ingress
    // chunks slower; keeping IDs first avoids the intermittent 400.
    formData.append('assessmentAssignedId', assessmentAssignedId)
    formData.append('studentId', studentId)
    formData.append('image', imageFile)
    
    await studentRequest.post(
      'students/assessments/proctoring/uploadImage',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    
    console.log('✅ [PROCTORING] Image uploaded successfully')
  } catch (error) {
    console.error('❌ [PROCTORING] Error processing proctoring image:', error)
  }
}
const blobToBase64 = blob => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1]
      resolve(base64String)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Add proctoring setup effect (around line 1800, after existing useEffects)
const studentData = useSelector(state => {
  return state.studentData?.studentDetails || state.auth?.user || null
})

useEffect(() => {
  if (!isAssessmentActive || isCompleted) {
    proctoringActive.current = false
    return
  }

  // FIXED: Get assessment_assigned_id with proper fallbacks
  const assessmentAssignedId = 
    currentAssessment?.assessment_assigned_id ||
    props.assessment?.assessment_assigned_id ||
    props.currentAssessment?.assessment_assigned_id ||
    location?.state?.assessmentData?.assessment_assigned_id ||
    location?.state?.assessment_assigned_id ||
    assessmentData?.assessment_assigned_id

  // FIXED: Get student ID using Redux store (same as aptitude assessment)
  const studentId = 
    studentData?.id ||
    studentData?.studentId ||
    studentData?.student_id ||
    props.studentDetails?.id ||
    props.studentDetails?.studentId ||
    props.studentDetails?.student_id ||
    currentAssessment?.studentId ||
    currentAssessment?.student_id

  // ponytail: studentId is optional for OTP-invite candidates (no portal login,
  // no redux studentData). The server resolves identity from assessmentAssignedId
  // -> primaryEmail, so only assessmentAssignedId is required to start proctoring.
  // Requiring it here skipped the whole snapshot interval for invite candidates,
  // leaving a single camera-guard frame as the only proctoring image.
  if (!assessmentAssignedId) {
    console.log('🔍 [PROCTORING] Missing assessmentAssignedId, skipping proctoring setup:', {
      assessmentAssignedId,
      studentData,
      currentAssessment,
      props: {
        assessment: props.assessment,
        studentDetails: props.studentDetails
      }
    })
    proctoringActive.current = false
    return
  }

  console.log('🔍 [PROCTORING] Starting proctoring with IDs:', {
    assessmentAssignedId,
    studentId: studentId || '(invite candidate — none)'
  })

  proctoringActive.current = true
  setupProctoringInterval()
  
  ;(async () => {
    try {
      const imageBlob = await captureImage()
      if (imageBlob) {
        await uploadProctorImage(imageBlob)
      }
    } catch (error) {
      console.error('❌ [PROCTORING] Error in initial capture:', error)
    }
  })()
  
  return () => {
    proctoringActive.current = false
    if (captureInterval.current) {
      clearTimeout(captureInterval.current)
      captureInterval.current = null
    }
    if (captureRef.current && captureRef.current.srcObject) {
      captureRef.current.srcObject.getTracks().forEach(track => {
        if (track.readyState === 'live') track.stop()
      })
      captureRef.current.srcObject = null
    }
  }
}, [
  isAssessmentActive,
  isCompleted,
  currentAssessment?.assessment_assigned_id,
  studentData?.id,
  props.assessment?.assessment_assigned_id,
  location?.state?.assessment_assigned_id,
])


// Add cleanup effect (around line 1850)

useEffect(() => {
  return () => {
    if (captureInterval.current) {
      clearTimeout(captureInterval.current)
      captureInterval.current = null
    }
    if (window.proctorStreamTracks) {
      window.proctorStreamTracks.forEach(track => {
        if (track.readyState === 'live') track.stop()
      })
      window.proctorStreamTracks = []
    }
    if (captureRef.current) {
      captureRef.current.srcObject = null
      captureRef.current = null
    }
  }
}, [])

  // Silently re-acquire the proctor camera in the background — no popup, no pause.
  const reacquiringCameraRef = useRef(false)
  const reacquireCameraSilently = useCallback(async () => {
    if (reacquiringCameraRef.current) return
    reacquiringCameraRef.current = true
    try {
      try {
        if (captureRef.current && captureRef.current.srcObject) {
          captureRef.current.srcObject.getTracks().forEach(t => { try { t.stop() } catch (_) {} })
        }
      } catch (e) { /* noop */ }
      captureRef.current = null
      window.proctorStreamTracks = []
      const blob = await captureImage()
      if (blob) await uploadProctorImage(blob)
    } catch (e) {
      console.warn('[camera-guard] silent re-acquire failed:', e?.message || e)
    } finally {
      reacquiringCameraRef.current = false
    }
  }, [])

  // While the camera is lost mid-assessment, keep retrying silently in the
  // background until it's back — the assessment keeps running uninterrupted.
  useEffect(() => {
    if (!cameraLost) return undefined
    reacquireCameraSilently()
    const id = setInterval(reacquireCameraSilently, 5000)
    return () => clearInterval(id)
  }, [cameraLost, reacquireCameraSilently])

  // Add fullscreen request function
  const requestFullscreenAccess = async () => {
    // Best-effort camera start: try to get a live camera before starting, but
    // don't block the assessment on it — the guard keeps retrying in the background.
    if (proctoringEnabled && !isCameraLive(captureRef.current && captureRef.current.srcObject)) {
      try { await captureImage() } catch (e) { /* fall through */ }
    }
    try {
      const element = document.documentElement

      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      setTimeout(() => {
        setIsAssessmentFullscreen(true)
        setShowFullscreenModal(false)
        setIsAssessmentActive(true)
        setProctoringReady(true)
      }, 500)
    } catch (error) {
      console.error('Error entering fullscreen:', error)
      setIsAssessmentFullscreen(true)
      setShowFullscreenModal(false)
      setIsAssessmentActive(true)
    }
  }

  // Add return to fullscreen handler
  const handleReturnToFullscreen = async () => {
    setShowReentryModal(false)

    try {
      const element = document.documentElement

      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      trackFullscreenEntered(
        currentAssessment?.assessment_assigned_id, 
        currentAssessment?.type, 
        true, 
        currentAssessment?.isCorporate, 
        currentAssessment?.entityName
      )
      setIsAssessmentFullscreen(true)
      setIsAssessmentActive(true)
      setProctoringReady(true)
    } catch (error) {
      console.error('Error re-entering fullscreen:', error)
      setIsAssessmentFullscreen(true)
      setIsAssessmentActive(true)
    }
  }

  // Lite-mode (iPhone) re-entry: there is no fullscreen to return to, so just
  // dismiss the warning and resume. The tab-switch/app-switch was still counted.
  const handleReturnLite = () => {
    setShowReentryModal(false)
    setIsAssessmentActive(true)
    setProctoringReady(true)
    // iPhone: iOS suspends the proctoring camera while backgrounded and rejects
    // a gesture-less getUserMedia re-acquire (from visibilitychange), so the
    // green light stays off on return. Re-acquire it HERE, inside the Resume
    // tap — a real user gesture iOS honours — so proctoring snapshots resume.
    try {
      if (captureRef.current && captureRef.current.srcObject) {
        captureRef.current.srcObject.getTracks().forEach(t => { try { t.stop() } catch (_) {} })
        captureRef.current.srcObject = null
      }
    } catch (_) {}
    captureRef.current = null
    try {
      if (window.proctorStreamTracks) {
        window.proctorStreamTracks.forEach(t => { try { t.stop() } catch (_) {} })
        window.proctorStreamTracks = []
      }
    } catch (_) {}
    if (currentAssessment?.allowProctoring !== false && !completedRef.current) {
      proctoringActive.current = true
      if (!captureInterval.current) setupProctoringInterval()
      captureImage().then(b => { if (b) uploadProctorImage(b) }).catch(() => {})
    }
  }

  // Add exit fullscreen function
  const exitFullscreen = async () => {
    try {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }

      setIsAssessmentFullscreen(false)
    } catch (error) {
      console.error('Error exiting fullscreen:', error)
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
      setIsAssessmentFullscreen(false)
    }
  }

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded)
  }

  const handleQuestionClick = (index) => {
    if (videoRecording) return
    setCurrentQuestionIndex(index)
  }

  const handleMCQOptionSelect = async (questionId, selectedOption, optionText) => {
    console.log('🔍 [MCQ] Saving response:', { questionId, selectedOption, optionText })
    // Report-only proctoring: correlate an answer right after a glance-back.
    if (proctoringCollectorStarted.current) {
      proctoringCollector.recordAnswer(questionId)
    }
    
    const assessmentId = currentAssessment?.assessment_assigned_id || 
                        props.assessment?.assessment_assigned_id ||
                        props.currentAssessment?.assessment_assigned_id ||
                        location.state?.assessmentData?.assessment_assigned_id ||
                        location.state?.assessment_assigned_id

    if (!assessmentId) {
      console.error('No assessment_assigned_id found for saving MCQ response')
      return
    }

    // Update local state immediately for UI feedback
    setResponses(prev => ({
      ...prev,
      [questionId]: { 
        answer: selectedOption, 
        option_text: optionText,
        status: 'attempted' 
      }
    }))

    try {
      await dispatch(
        saveQuestionResponse(
          questionId,
          selectedOption,
          assessmentId,
          null,
          'mcq',
          0
        )
      )
      
      console.log('✅ [MCQ] Response saved successfully')
    } catch (error) {
      console.error('❌ [MCQ] Error saving response:', error)
    }
  }

  const handleSubjectiveResponseChange = (questionId, value) => {
    setSubjectiveResponses(prev => ({
      ...prev,
      [questionId]: value
    }))
    
    setResponses(prev => ({
      ...prev,
      [questionId]: { 
        answer: value,
        status: value.trim() ? 'attempted' : 'unanswered'
      }
    }))
  }

  const saveSubjectiveResponse = async (questionId) => {
    const response = subjectiveResponses[questionId]
    
    if (!response || !response.trim()) {
      console.log('⚠️ [Subjective] No response to save for question:', questionId)
      return
    }

    const assessmentId = currentAssessment?.assessment_assigned_id || 
                        props.assessment?.assessment_assigned_id ||
                        props.currentAssessment?.assessment_assigned_id ||
                        location.state?.assessmentData?.assessment_assigned_id ||
                        location.state?.assessment_assigned_id

    if (!assessmentId) {
      console.error('No assessment_assigned_id found for saving subjective response')
      return
    }

    console.log('🔍 [Subjective] Saving response:', { questionId, response: response.trim() })

    try {
      const requestBody = {
        payload: {
          questionId: questionId,
          answer: response.trim(),
          assessment_assigned_id: assessmentId,
          timeTaken: 0
        }
      }
      
      const apiResponse = await studentRequest.post('students/assessments/saveResponse', requestBody)
      console.log('✅ [Subjective] Response saved successfully:', apiResponse)
    } catch (error) {
      console.error('❌ [Subjective] Error saving response:', error)
    }
  }




  const handleCodingResponseChange = (questionId, value) => {
    setCodingResponses(prev => ({
      ...prev,
      [questionId]: value
    }))

    setResponses(prev => ({
      ...prev,
      [questionId]: {
        answer: value,
        status: value.trim() ? 'attempted' : 'unanswered'
      }
    }))
  }

  const handleLanguageChange = (questionId, language) => {
    setSelectedLanguage(prev => ({
      ...prev,
      [questionId]: language
    }))
    // Load starter code for the new language if user hasn't written anything yet
    const question = questions.find(q => q.id === questionId)
    const currentCode = codingResponses[questionId] || ''
    const starterCodes = question?.coding_metadata?.starter_code
    if (starterCodes && typeof starterCodes === 'object') {
      // Check if current code is a starter code (hasn't been modified) — if so, swap it
      const isStarter = Object.values(starterCodes).some(sc => sc === currentCode.trim())
      const isEmpty = !currentCode.trim() || currentCode.trim() === 'pass'
      if (isEmpty || isStarter) {
        const newStarter = starterCodes[language] || ''
        if (newStarter) {
          setCodingResponses(prev => ({ ...prev, [questionId]: newStarter }))
        }
      }
    }
  }

  // Pick the language for a coding response. Never silently fall back to
  // 'javascript' — that mismatches Python questions and pins every answer
  // to the wrong backend evaluator. Also never pick a language the backend
  // can't actually score. Priority:
  //   1. user selection in this session (kept even if outside the eval set —
  //      the backend will mark it unsupported instead of silently scoring 0)
  //   2. question's coding_metadata.languages[0], filtered to evaluated set
  //   3. question's coding_metadata.language (if evaluated)
  //   4. 'python' (the broadest evaluator coverage)
  const EVALUATED_LANGUAGES = ['python', 'javascript', 'typescript', 'java', 'cpp', 'c']
  const resolveCodingLanguage = (questionId) => {
    if (selectedLanguage[questionId]) return selectedLanguage[questionId]
    const q = questions.find(qq => qq.id === questionId)
    const langs = (q?.coding_metadata?.languages || []).filter(l => EVALUATED_LANGUAGES.includes(l))
    if (langs.length) return langs[0]
    if (q?.coding_metadata?.language && EVALUATED_LANGUAGES.includes(q.coding_metadata.language)) {
      return q.coding_metadata.language
    }
    return 'python'
  }

  const saveCodingResponse = async (questionId, { retries = 2 } = {}) => {
    const code = codingResponses[questionId]

    if (!code || !code.trim()) {
      console.log('⚠️ [Coding] No code to save for question:', questionId)
      return
    }

    const assessmentId = currentAssessment?.assessment_assigned_id ||
                        props.assessment?.assessment_assigned_id ||
                        props.currentAssessment?.assessment_assigned_id ||
                        location.state?.assessmentData?.assessment_assigned_id ||
                        location.state?.assessment_assigned_id

    if (!assessmentId) {
      console.error('No assessment_assigned_id found for saving coding response')
      setCodingSaveStatus(prev => ({ ...prev, [questionId]: { state: 'error', message: 'No assessment ID — your answer is not being saved.' } }))
      return
    }

    const language = resolveCodingLanguage(questionId)
    console.log('🔍 [Coding] Saving response:', { questionId, language })

    setCodingSaveStatus(prev => ({ ...prev, [questionId]: { state: 'saving' } }))

    const answerPayload = JSON.stringify({
      code: code.trim(),
      language,
      test_results: (codeOutput[questionId]?.testResults || []).map(r => ({
        input: r.input,
        expected: r.expected,
        actual: r.actual,
        passed: r.passed,
        label: r.label,
      }))
    })

    const requestBody = {
      payload: {
        questionId: questionId,
        answer: answerPayload,
        assessment_assigned_id: assessmentId,
        timeTaken: 0
      }
    }

    let lastError = null
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const apiResponse = await studentRequest.post('students/assessments/saveResponse', requestBody)
        console.log(`✅ [Coding] Response saved successfully (attempt ${attempt + 1}):`, apiResponse)
        setCodingSaveStatus(prev => ({ ...prev, [questionId]: { state: 'saved' } }))
        return
      } catch (error) {
        lastError = error
        console.error(`❌ [Coding] Save failed (attempt ${attempt + 1}/${retries + 1}):`, error)
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)))
        }
      }
    }
    setCodingSaveStatus(prev => ({
      ...prev,
      [questionId]: {
        state: 'error',
        message: `Couldn't save your code (${lastError?.message || 'network error'}). It will be retried on the next save.`
      }
    }))
  }

  // Extract the function name from code (supports Python and JavaScript)
  const extractFunctionName = (code, language = 'python') => {
    if (language === 'python') {
      const match = code.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)
      return match ? match[1] : null
    } else if (language === 'javascript') {
      const match = code.match(/function\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/)
      return match ? match[1] : null
    } else if (language === 'java') {
      // Match public/private/static methods but not class declarations
      const match = code.match(/(?:public|private|static)\s+\S+\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)
      return match ? match[1] : null
    } else if (language === 'cpp') {
      const match = code.match(/(?:int|void|string|bool|double|float|vector|auto)\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)
      return match ? match[1] : null
    }
    return null
  }

  // Strip calling code and keep only function/import definitions
  const extractFunctionBody = (code, language = 'python') => {
    if (language === 'python') {
      const lines = code.split('\n')
      const functionLines = []
      let insideFunction = false
      let indentLevel = 0

      for (const line of lines) {
        const trimmed = line.trimStart()
        const currentIndent = line.length - line.trimStart().length

        if (trimmed.startsWith('def ')) {
          insideFunction = true
          indentLevel = currentIndent
          functionLines.push(line)
        } else if (insideFunction) {
          if (trimmed === '' || currentIndent > indentLevel) {
            functionLines.push(line)
          } else if (trimmed.startsWith('@') || trimmed.startsWith('def ')) {
            insideFunction = true
            indentLevel = currentIndent
            functionLines.push(line)
          } else {
            insideFunction = false
          }
        } else if (trimmed.startsWith('import ') || trimmed.startsWith('from ') || trimmed.startsWith('#') || trimmed === '') {
          functionLines.push(line)
        }
      }
      return functionLines.join('\n')
    } else {
      // For JS/Java/C++: use brace counting to extract function body
      const lines = code.split('\n')
      const functionLines = []
      let insideFunction = false
      let braceDepth = 0

      for (const line of lines) {
        const trimmed = line.trim()
        if (!insideFunction) {
          // Check if this line starts a function
          if (trimmed.match(/^(function\s|const\s+\w+\s*=|let\s+\w+\s*=|var\s+\w+\s*=|public\s|private\s|static\s|int\s|void\s|string\s|bool\s|auto\s|vector)/) || trimmed.match(/^(\/\/|\/\*|\*|#include|import\s|using\s)/)) {
            insideFunction = true
            functionLines.push(line)
            braceDepth += (line.split('{').length - 1) - (line.split('}').length - 1)
          }
        } else {
          functionLines.push(line)
          braceDepth += (line.split('{').length - 1) - (line.split('}').length - 1)
          if (braceDepth <= 0) {
            insideFunction = false
            braceDepth = 0
          }
        }
      }
      return functionLines.join('\n')
    }
  }

  // Deep equality comparison for test case outputs (like LeetCode)
  const deepEqual = (a, b) => {
    if (a === b) return true
    if (a == null || b == null) return false
    if (typeof a !== typeof b) return false
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false
      return a.every((v, i) => deepEqual(v, b[i]))
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a).sort()
      const keysB = Object.keys(b).sort()
      if (keysA.length !== keysB.length) return false
      return keysA.every((k, i) => keysA[i] === keysB[i] && deepEqual(a[k], b[k]))
    }
    // Compare numbers with tolerance for floats
    if (typeof a === 'number' && typeof b === 'number') {
      return Math.abs(a - b) < 0.0001
    }
    return false
  }

  // Compare test output: try parsing as JSON for structural comparison, fallback to string
  const compareOutput = (actual, expected) => {
    // Strip wrapping quotes (example outputs may have 'value' or "value")
    const stripQuotes = s => s.replace(/^['"]/, '').replace(/['"]$/, '')
    const a = stripQuotes((actual || '').trim())
    const e = stripQuotes((expected || '').trim())
    if (!e) return true
    // Exact string match
    if (a === e) return true
    // Try JSON deep comparison
    try {
      const parsedA = JSON.parse(a.replace(/'/g, '"').replace(/True/g, 'true').replace(/False/g, 'false').replace(/None/g, 'null'))
      const parsedE = JSON.parse(e.replace(/'/g, '"').replace(/True/g, 'true').replace(/False/g, 'false').replace(/None/g, 'null'))
      return deepEqual(parsedA, parsedE)
    } catch {
      // Normalize whitespace and quotes for simple comparison
      const normalizeStr = s => s.replace(/\s+/g, ' ').replace(/'/g, '"').trim()
      return normalizeStr(a) === normalizeStr(e)
    }
  }

  // Parse test input args (handles "nums = [1,2], target = 3" format)
  const parseTestArgs = (testInput) => {
    let callArgs = testInput
    if (testInput.includes('=')) {
      const parts = []
      let depth = 0, current = ''
      for (const ch of testInput) {
        if ('([{'.includes(ch)) depth++
        else if (')]}'.includes(ch)) depth--
        if (ch === ',' && depth === 0) {
          parts.push(current.trim())
          current = ''
        } else {
          current += ch
        }
      }
      if (current.trim()) parts.push(current.trim())
      callArgs = parts.map(p => p.includes('=') ? p.split('=').slice(1).join('=').trim() : p).join(', ')
    }
    return callArgs
  }

  // Build wrapper code that calls the function with test case input
  const buildTestWrapper = (functionBody, funcName, testInput, language = 'python') => {
    const callArgs = parseTestArgs(testInput)

    if (language === 'python') {
      return `${functionBody}\n\nprint(${funcName}(${callArgs}))`
    } else if (language === 'javascript') {
      return `${functionBody}\n\nconsole.log(${funcName}(${callArgs}))`
    } else if (language === 'java') {
      return `${functionBody}\n// Auto-generated test\nSystem.out.println(${funcName}(${callArgs}));`
    } else if (language === 'cpp') {
      return `${functionBody}\n// Auto-generated test\ncout << ${funcName}(${callArgs}) << endl;`
    }
    return `${functionBody}\n\nprint(${funcName}(${callArgs}))`
  }

  const runCode = async (questionId) => {
    const code = codingResponses[questionId]
    if (!code || !code.trim()) return

    const language = selectedLanguage[questionId] || 'python'

    // Get test cases from question metadata
    const question = questions.find(q => q.id === questionId)
    const testCases = question?.coding_metadata?.test_cases || []
    const examples = question?.coding_metadata?.examples || []
    const starterCodeObj = question?.coding_metadata?.starter_code || ''
    const starterCode = typeof starterCodeObj === 'string' ? starterCodeObj : (starterCodeObj[language] || Object.values(starterCodeObj)[0] || '')

    // Extract function name from starter code or student code
    const funcName = extractFunctionName(starterCode, language) || extractFunctionName(code, language)

    // Combine examples and test cases for running
    const allCases = [
      ...examples.map((ex, i) => ({ input: ex.input, expected_output: (ex.output || '').replace(/^['"]|['"]$/g, ''), label: `Example ${i + 1}`, is_hidden: false })),
      ...testCases.map((tc, i) => ({ input: tc.input, expected_output: tc.expected_output, label: `Test Case ${i + 1}`, is_hidden: tc.is_hidden || false })),
    ]

    if (allCases.length === 0) {
      allCases.push({ input: '', expected_output: '', label: 'Run' })
    }

    setCodeRunning(prev => ({ ...prev, [questionId]: true }))
    setCodeOutput(prev => ({ ...prev, [questionId]: { status: 'running', output: 'Running code against test cases...' } }))

    try {
      const results = []
      let passedCount = 0

      // Extract only the function definitions from student code (strip print/console.log calls etc.)
      const functionBody = funcName ? extractFunctionBody(code, language) : code

      for (const testCase of allCases) {
        try {
          // Build wrapped code: student's function + print(func(test_input))
          const wrappedCode = funcName
            ? buildTestWrapper(functionBody, funcName, testCase.input || '', language)
            : code

          const response = await studentRequest.post('/assessment/execute-code', {
            code: wrappedCode,
            language,
            stdin: '',
          })

          const result = response.data?.data || response.data

          if (result.stderr && result.exitCode !== 0) {
            results.push({
              label: testCase.label,
              passed: false,
              input: testCase.is_hidden ? '(hidden)' : testCase.input,
              expected: testCase.is_hidden ? '(hidden)' : testCase.expected_output,
              actual: testCase.is_hidden ? '(hidden)' : result.stderr,
              isError: true,
              is_hidden: testCase.is_hidden,
            })
          } else {
            const actual = (result.stdout || '').trim()
            const expected = (testCase.expected_output || '').trim()
            const passed = compareOutput(actual, expected)
            if (passed) passedCount++

            results.push({
              label: testCase.label,
              passed,
              input: testCase.is_hidden ? '(hidden)' : testCase.input,
              expected: testCase.is_hidden ? '(hidden)' : testCase.expected_output,
              actual: testCase.is_hidden ? '(hidden)' : (result.stdout || '(no output)'),
              isError: false,
              is_hidden: testCase.is_hidden,
            })
          }
        } catch (err) {
          results.push({
            label: testCase.label,
            passed: false,
            input: testCase.is_hidden ? '(hidden)' : testCase.input,
            expected: testCase.is_hidden ? '(hidden)' : testCase.expected_output,
            actual: testCase.is_hidden ? '(hidden)' : (err.message || 'Execution failed'),
            isError: true,
            is_hidden: testCase.is_hidden,
          })
        }
      }

      setCodeOutput(prev => ({
        ...prev,
        [questionId]: {
          status: passedCount === allCases.length ? 'passed' : 'partial',
          output: `${passedCount}/${allCases.length} test cases passed`,
          testResults: results,
          passedCount,
          totalCount: allCases.length,
        }
      }))
    } catch (err) {
      console.error('Code execution error:', err)
      setCodeOutput(prev => ({
        ...prev,
        [questionId]: {
          status: 'error',
          output: err.message || 'Failed to execute code',
        }
      }))
    } finally {
      setCodeRunning(prev => ({ ...prev, [questionId]: false }))
    }
  }

const formatVideoTime = (seconds) => {
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const getVideoAttempts = (questionId) => {
  return videoRemainingAttempts[questionId] !== undefined ? videoRemainingAttempts[questionId] : 2
}

const startVideoRecording = async (questionId) => {
  try {
    console.log('🎥 [Video] Starting recording for question:', questionId)

    // SHARED STREAM (matches the validated test-page flow): record from the SAME
    // live proctoring stream — do NOT stop proctoring or open a second camera, so
    // proctoring keeps capturing snapshots throughout the recording.
    setVideoRecordingTimer(0)
    setVideoMinReached(false)
    setVideoRecording(questionId)
    videoRecordingRef.current = questionId
    interruptedVideoRef.current = false

    let mediaStream = captureRef.current && captureRef.current.srcObject
    const hasLiveVideo = mediaStream && mediaStream.getVideoTracks().some(t => t.readyState === 'live')
    if (!hasLiveVideo) {
      mediaStream = await getUserMediaWithSelectedDevices({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: { sampleRate: 44100, channelCount: 1, echoCancellation: true, noiseSuppression: true },
      })
      if (!captureRef.current) {
        captureRef.current = document.createElement('video')
        mountHiddenProctorVideo(captureRef.current)
        captureRef.current.srcObject = mediaStream
        captureRef.current.autoplay = true
        captureRef.current.muted = true
        await captureRef.current.play().catch(() => {})
        if (!window.proctorStreamTracks) window.proctorStreamTracks = []
        mediaStream.getTracks().forEach(t => window.proctorStreamTracks.push(t))
        proctoringActive.current = true
        if (!captureInterval.current) setupProctoringInterval()
      }
    }

    console.log('🎥 [Video] MediaStream obtained successfully')

    setVideoStreams(prev => ({
      ...prev,
      [questionId]: mediaStream
    }))

    // Setup MediaRecorder with optimal settings (before timer so we can reference recorder)
    const mimeTypes = [
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ]
    let selectedMimeType = ''
    for (const type of mimeTypes) {
      if (typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(type)) {
        selectedMimeType = type
        break
      }
    }

    const recorderOptions = {
      videoBitsPerSecond: 500000,
      audioBitsPerSecond: 64000,
    }
    if (selectedMimeType) recorderOptions.mimeType = selectedMimeType

    let recorder
    try {
      recorder = new MediaRecorder(mediaStream, selectedMimeType ? recorderOptions : undefined)
    } catch (e) {
      console.log('Falling back to default MediaRecorder options:', e)
      recorder = new MediaRecorder(mediaStream)
    }

    videoChunks.current[questionId] = []

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        if (!videoChunks.current[questionId]) videoChunks.current[questionId] = []
        videoChunks.current[questionId].push(e.data)
      }
    }

    recorder.onstop = async () => {
      console.log('🎥 [Video] Recording stopped for question:', questionId)

      const wasInterrupted = interruptedVideoRef.current
      interruptedVideoRef.current = false
      videoRecordingRef.current = false
      activeVideoRecorderRef.current = null

      // Clear timer
      if (videoTimerRef.current) {
        clearInterval(videoTimerRef.current)
        videoTimerRef.current = null
      }

      if (!wasInterrupted) {
        const finalMimeType = recorder.mimeType || selectedMimeType || 'video/webm'
        const chunks = videoChunks.current[questionId] || []
        const blob = new Blob(chunks, { type: finalMimeType })
        const url = URL.createObjectURL(blob)

        console.log(`📹 [VIDEO] Recorded video size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`)

        setRecordedVideos(prev => ({
          ...prev,
          [questionId]: url
        }))

        setVideoBlobs(prev => ({
          ...prev,
          [questionId]: blob
        }))

        // Decrement attempts
        setVideoRemainingAttempts(prev => ({
          ...prev,
          [questionId]: (prev[questionId] !== undefined ? prev[questionId] : 2) - 1
        }))

        // SHARED STREAM: do NOT stop tracks — this is the live proctoring stream.
        setVideoStreams(prev => {
          const newStreams = { ...prev }
          delete newStreams[questionId]
          return newStreams
        })

        setVideoRecording(false)
        setVideoRecordingTimer(0)
        setVideoMinReached(false)
        videoChunks.current[questionId] = []

        await saveVideoResponse(questionId, blob)
      } else {
        // iPhone: iOS suspended the camera mid-recording (tab/app switch), so
        // this take is partial/corrupt — discard it (don't store, upload, or
        // burn an attempt) and reset to the ready-to-record state. The re-entry
        // warning already told the candidate they left the screen.
        console.log('🎥 [VIDEO] Recording interrupted by background; discarding partial take for question:', questionId)
        setVideoStreams(prev => {
          const newStreams = { ...prev }
          delete newStreams[questionId]
          return newStreams
        })
        setVideoRecording(false)
        setVideoRecordingTimer(0)
        setVideoMinReached(false)
        videoChunks.current[questionId] = []
      }

      // SHARED STREAM: proctoring never stopped, so there is nothing to restart.
    }

    setVideoRecorders(prev => ({
      ...prev,
      [questionId]: recorder
    }))
    activeVideoRecorderRef.current = recorder

    recorder.start()
    console.log('🎥 [Video] Recording started successfully')

    // Start the timer after recorder is created
    let elapsed = 0
    videoTimerRef.current = setInterval(() => {
      elapsed += 1
      setVideoRecordingTimer(elapsed)
      if (elapsed >= MIN_VIDEO_RECORDING_TIME) {
        setVideoMinReached(true)
      }
      // Auto-stop at max time using recorder directly
      if (elapsed >= MAX_VIDEO_RECORDING_TIME) {
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop()
        }
        clearInterval(videoTimerRef.current)
        videoTimerRef.current = null
      }
    }, 1000)

  } catch (error) {
    console.error('❌ [Video] Error starting recording:', error)
    setVideoRecording(false)
    setVideoRecordingTimer(0)
    videoRecordingRef.current = false
    activeVideoRecorderRef.current = null
    interruptedVideoRef.current = false
    if (videoTimerRef.current) {
      clearInterval(videoTimerRef.current)
      videoTimerRef.current = null
    }

    // Resume proctoring if recording failed to start
    console.log('↩️ [RECORD_FAIL] Resuming proctoring after failed recording start')
    proctoringActive.current = true
    if (
      !captureInterval.current &&
      currentAssessment?.allowProctoring !== false &&
      isAssessmentActive &&
      !isCompleted
    ) {
      setupProctoringInterval()
    }

    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      alert('Camera may be in use. Please try again momentarily.')
    } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      alert('Camera permission denied. Please allow camera access in your browser settings.')
    } else {
      alert('Error accessing camera. Please check your camera permissions and try again.')
    }
  }
}

  const stopVideoRecording = (questionId) => {
    console.log('🎥 [Video] Stopping recording for question:', questionId)

    const recorder = videoRecorders[questionId]
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
    }
  }

  const handleVideoDeleteClick = (questionId) => {
    setVideoDeleteModalQuestion(questionId)
  }

  const handleVideoDeleteClose = () => {
    setVideoDeleteModalQuestion(null)
  }

  const handleVideoConfirmDelete = () => {
    const questionId = videoDeleteModalQuestion
    if (questionId && recordedVideos[questionId]) {
      URL.revokeObjectURL(recordedVideos[questionId])
      setRecordedVideos(prev => {
        const updated = { ...prev }
        delete updated[questionId]
        return updated
      })
      setVideoBlobs(prev => {
        const updated = { ...prev }
        delete updated[questionId]
        return updated
      })
    }
    setVideoDeleteModalQuestion(null)
    setVideoShowSuccessModal(true)
  }

  const handleVideoSuccessModalClose = () => {
    setVideoShowSuccessModal(false)
  }

const saveVideoResponse = async (questionId, videoBlob) => {
  console.log('🎥 [Video] saveVideoResponse called for question:', questionId)
  console.log('🎥 [Video] Blob size:', videoBlob?.size, 'bytes')
  
  const assessmentId = currentAssessment?.assessment_assigned_id || 
                      props.assessment?.assessment_assigned_id ||
                      props.currentAssessment?.assessment_assigned_id ||
                      location.state?.assessmentData?.assessment_assigned_id ||
                      location.state?.assessment_assigned_id

  if (!assessmentId) {
    console.error('❌ [Video] No assessment_assigned_id found for saving video response')
    return null
  }

  if (!videoBlob || videoBlob.size === 0) {
    console.error('❌ [Video] Invalid video blob')
    return null
  }

  try {
    console.log('🎥 [Video] Dispatching uploadVideoResponse...')
    // Pass assessmentId explicitly so the backend uploadVideo handler can
    // upsert the StudentAnswer row at upload time. Without it the handler
    // hits its "skipping DB storage" branch and the cron later scores 0
    // because no row exists for the cron's retry loop to find.
    const uploadPromise = dispatch(uploadVideoResponse(videoBlob, questionId, assessmentId))
    inflightVideoUploadsRef.current.track(questionId, uploadPromise)
    const uploadResponse = await uploadPromise
    console.log('🎥 [Video] Upload response received:', uploadResponse)
    console.log('🎥 [Video] Upload response type:', typeof uploadResponse)
    
    let videoKey = null
    
    if (typeof uploadResponse === 'string') {
      console.log('🎥 [Video] Response is string:', uploadResponse)
      if (uploadResponse.includes('/')) {
        const parts = uploadResponse.split('/')
        videoKey = parts[parts.length - 1]
        
        if (videoKey.includes('?')) {
          videoKey = videoKey.split('?')[0]
        }
      } else {
        videoKey = uploadResponse
      }
    } else if (typeof uploadResponse === 'object' && uploadResponse !== null) {
      console.log('🎥 [Video] Response is object, keys:', Object.keys(uploadResponse))
      videoKey = uploadResponse.key || 
                 uploadResponse.objectKey || 
                 uploadResponse.videoKey ||
                 uploadResponse.fileName ||
                 uploadResponse.name ||
                 null
      
      if (videoKey && videoKey.includes('/')) {
        const parts = videoKey.split('/')
        videoKey = parts[parts.length - 1]
        
        if (videoKey.includes('?')) {
          videoKey = videoKey.split('?')[0]
        }
      }
    }
    
    console.log('🎥 [Video] Extracted video key:', videoKey)
    
    if (videoKey) {
      console.log('🎥 [Video] Updating responses state for question:', questionId)
      setResponses(prev => {
        const newResponses = {
          ...prev,
          [questionId]: { 
            answer: videoKey,
            status: 'attempted'
          }
        }
        console.log('🎥 [Video] New responses state:', newResponses)
        return newResponses
      })
      
      console.log('✅ [Video] Video uploaded successfully. Key:', videoKey)
      return videoKey
    } else {
      console.error('❌ [Video] Could not extract video key from upload response:', uploadResponse)
      return null
    }
  } catch (error) {
    console.error('❌ [Video] Error uploading video:', error)
    console.error('❌ [Video] Error stack:', error.stack)
    return null
  }
}

  const handleNext = async () => {
    const currentQuestion = questions[currentQuestionIndex]
    
    if (!currentQuestion) return

    if (currentQuestion.section_name === "Subjective Question") {
      await saveSubjectiveResponse(currentQuestion.id)
    }
    if (currentQuestion.section_name === "Coding Question") {
      await saveCodingResponse(currentQuestion.id)
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleSkip = () => {
    setResponses(prev => ({
      ...prev,
      [questions[currentQuestionIndex].id]: { status: 'skipped' }
    }))
    handleNext()
  }
const stopAllMediaStreams = () => {
    proctoringActive.current = false
    if (captureInterval.current) {
      clearTimeout(captureInterval.current)
      captureInterval.current = null
    }
    if (captureRef.current && captureRef.current.srcObject) {
      try {
        captureRef.current.srcObject.getTracks().forEach(track => track.stop())
      } catch (e) { /* noop */ }
      captureRef.current.srcObject = null
    }
    captureRef.current = null
    if (window.proctorStreamTracks) {
      window.proctorStreamTracks.forEach(track => {
        try { track.stop() } catch (e) { /* noop */ }
      })
      window.proctorStreamTracks = []
    }
    try {
      Object.values(videoStreams || {}).forEach(s => {
        if (s && s.getTracks) s.getTracks().forEach(track => track.stop())
      })
    } catch (e) { /* noop */ }
  }

const handleSubmit = useCallback(async () => {
    console.log('🔍 [Submit] Starting assessment submission...')

    // Flush any in-progress video recordings BEFORE killing media streams.
    // On auto-submit (timer expiry), the active MediaRecorder hasn't been
    // stopped, so its onstop -> saveVideoResponse upload never runs. We must
    // call recorder.stop() and await the existing onstop (which awaits the
    // S3 upload) before stopAllMediaStreams() tears down the tracks.
    const activeRecorders = Object.entries(videoRecorders || {}).filter(
      ([, recorder]) => recorder && recorder.state === 'recording'
    )
    if (activeRecorders.length > 0) {
      console.log(`🎥 [Submit] Flushing ${activeRecorders.length} active recorder(s) before stream teardown`)
      await Promise.all(
        activeRecorders.map(([questionId, recorder]) =>
          new Promise(resolve => {
            const previousOnStop = recorder.onstop
            recorder.onstop = async (event) => {
              try {
                if (typeof previousOnStop === 'function') {
                  await previousOnStop.call(recorder, event)
                }
              } catch (e) {
                console.error(`🎥 [Submit] onstop handler failed for question ${questionId}:`, e)
              } finally {
                resolve()
              }
            }
            try {
              recorder.stop()
            } catch (e) {
              console.error(`🎥 [Submit] recorder.stop() failed for question ${questionId}:`, e)
              resolve()
            }
          })
        )
      )
      console.log('🎥 [Submit] All active recordings finalized')
    }

    // Drain any in-flight video uploads from earlier recordings whose
    // onstop fired but whose HTTP POST was still in transit when this
    // submit started. The flush block above covers recordings we just
    // stopped; this catches the rest. drain() uses Promise.allSettled
    // internally — a single failed upload never aborts submission.
    const inflightCount = inflightVideoUploadsRef.current.size()
    if (inflightCount > 0) {
      console.log(`📤 [Submit] Awaiting ${inflightCount} in-flight video upload(s) to settle`)
      await inflightVideoUploadsRef.current.drain()
      console.log('📤 [Submit] All in-flight uploads settled')
    }

    // Stop camera/proctoring streams immediately on submit
    stopAllMediaStreams()

    // FIXED: Update refs immediately before any async operations
    completedRef.current = true
    assessmentActiveRef.current = false

    // Then update state
    setShowFullscreenModal(false)
    setIsCompleted(true)
    setIsAssessmentActive(false)
    
    // Small delay to ensure state propagation before exiting fullscreen
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // Exit fullscreen after refs and state are updated
    await exitFullscreen()
    
    const currentQuestion = questions[currentQuestionIndex]
    if (currentQuestion && currentQuestion.section_name === "Subjective Question") {
      await saveSubjectiveResponse(currentQuestion.id)
    }
    if (currentQuestion && currentQuestion.section_name === "Coding Question") {
      await saveCodingResponse(currentQuestion.id)
    }

    const assessmentId = currentAssessment?.assessment_assigned_id || 
                        props.assessment?.assessment_assigned_id ||
                        props.currentAssessment?.assessment_assigned_id ||
                        location.state?.assessmentData?.assessment_assigned_id ||
                        location.state?.assessment_assigned_id

    if (!assessmentId) {
      console.error('No assessment_assigned_id found for submission')
      alert('Assessment ID not found. Cannot submit assessment.')
      return
    }

    try {
      const mcqQuestions = []
      const subjectiveQuestions = []
      const videoResponses = []
      const codingQuestions = []

      // Read through refs so the just-finalized recorder's setResponses /
      // setVideoBlobs (run during the await Promise.all flush above) are
      // visible here. Closure-captured `responses` / `videoBlobs` lag.
      const liveResponses = responsesRef.current || {}
      const liveVideoBlobs = videoBlobsRef.current || {}

      console.log('🔍 [Submit] Processing questions...')
      console.log('🔍 [Submit] Total questions:', questions.length)
      console.log('🔍 [Submit] Responses:', liveResponses)
      console.log('🔍 [Submit] Video blobs:', Object.keys(liveVideoBlobs))

for (const question of questions) {
  const response = liveResponses[question.id]

  console.log(`🔍 [Submit] Processing question ${question.id} (${question.section_name}):`, response)
  console.log(`🔍 [Submit] Question has video blob:`, !!liveVideoBlobs[question.id])

  // Special handling for Video Response questions
  if (question.section_name === "Video Response") {
    console.log(`🎥 [Submit] Processing Video Response for question ${question.id}`)
    console.log(`🎥 [Submit] Response:`, response)
    console.log(`🎥 [Submit] Response answer:`, response?.answer)
    console.log(`🎥 [Submit] Video blob exists:`, !!liveVideoBlobs[question.id])
    console.log(`🎥 [Submit] Video blob size:`, liveVideoBlobs[question.id]?.size)

    // FIXED: Check if video was already uploaded (answer exists in response)
    if (response && response.answer && typeof response.answer === 'string') {
      let videoKey = response.answer
      
      console.log(`🎥 [Submit] Using already uploaded video key:`, videoKey)
      
      // Clean up the video key (remove path and query params)
      if (videoKey.includes('/')) {
        const parts = videoKey.split('/')
        videoKey = parts[parts.length - 1]
      }
      
      if (videoKey.includes('?')) {
        videoKey = videoKey.split('?')[0]
      }
      
      console.log(`🎥 [Submit] Cleaned video key:`, videoKey)
      
      videoResponses.push({
        questionId: question.id,
        objectKey: videoKey
      })
      
      console.log(`✅ [Submit] Added video response for question ${question.id} with key ${videoKey}`)
    } 
    // Fallback: If no answer in response but blob exists, upload now
    else if (liveVideoBlobs[question.id]) {
      console.log(`🎥 [Submit] No answer in response state, uploading blob now for question ${question.id}`)

      const videoKey = await saveVideoResponse(question.id, liveVideoBlobs[question.id])
      
      if (videoKey) {
        videoResponses.push({
          questionId: question.id,
          objectKey: videoKey
        })
        console.log(`✅ [Submit] Uploaded and added video response for question ${question.id} with key ${videoKey}`)
      } else {
        console.error(`❌ [Submit] Failed to upload video for question ${question.id}`)
      }
    } else {
      console.error(`❌ [Submit] No video answer or blob for question ${question.id}`)
      console.error(`❌ [Submit] Available video blob keys:`, Object.keys(liveVideoBlobs))
      console.error(`❌ [Submit] Available response keys:`, Object.keys(liveResponses))
    }
    // Continue to next question (don't check response.answer again)
    continue
  }
  
  // Handle Coding Questions separately (stored in codingResponses, not responses)
  if (question.section_name === "Coding Question") {
    const codeAnswer = codingResponses[question.id]
    if (codeAnswer && codeAnswer.trim()) {
      const answerPayload = JSON.stringify({
        code: codeAnswer.trim(),
        language: resolveCodingLanguage(question.id),
        test_results: (codeOutput[question.id]?.testResults || []).map(r => ({
          input: r.input,
          expected: r.expected,
          actual: r.actual,
          passed: r.passed,
          label: r.label,
        }))
      })

      // Save coding response before final submit
      try {
        await studentRequest.post('students/assessments/saveResponse', {
          payload: {
            questionId: question.id,
            answer: answerPayload,
            assessment_assigned_id: assessmentId,
            timeTaken: 0
          }
        })
        console.log(`✅ [Submit] Coding response saved for question ${question.id}`)
      } catch (err) {
        console.error(`❌ [Submit] Failed to save coding response for question ${question.id}:`, err)
      }

      codingQuestions.push({
        questionId: question.id,
        answer: answerPayload
      })
      console.log(`✅ [Submit] Adding Coding question ${question.id}`)
    } else {
      console.log(`⚠️ [Submit] Skipping coding question ${question.id} - no code written`)
    }
    continue
  }

  // For non-video, non-coding questions, check if answer exists
  if (!response || !response.answer) {
    console.log(`⚠️ [Submit] Skipping question ${question.id} - no response`)
    continue
  }

  if (question.section_name === "MCQ Question") {
    console.log(`✅ [Submit] Adding MCQ question ${question.id}`)
    mcqQuestions.push({
      questionId: question.id,
      answer: response.answer
    })
  } else if (question.section_name === "Subjective Question") {
    console.log(`✅ [Submit] Adding Subjective question ${question.id}`)
    subjectiveQuestions.push({
      questionId: question.id,
      answer: response.answer
    })
  }
}

      const totalTakenTime = calculateTotalTime()

      const submissionPayload = {
        assessment_assigned_id: assessmentId,
        response: {
          mcqQuestions,
          subjectiveQuestions,
          videoResponses,
          codingQuestions
        },
        full_name: props.studentDetails?.full_name ||
                  props.studentDetails?.name ||
                  currentAssessment?.studentDetails?.full_name ||
                  'Student',
        totalTakenTime: totalTakenTime,
        isAutoSubmitted: isAutoSubmittedRef.current,
      }

      console.log('🔍 [Submit] Final submission payload:', JSON.stringify(submissionPayload, null, 2))
      console.log('🔍 [Submit] Video responses count:', videoResponses.length)
      console.log('🔍 [Submit] MCQ responses count:', mcqQuestions.length)
      console.log('🔍 [Submit] Subjective responses count:', subjectiveQuestions.length)
      console.log('🔍 [Submit] Coding responses count:', codingQuestions.length)

      try {
        // Scope persistence by assessment_assigned_id so a previous
        // submission's flag never bleeds into a different assessment.
        // Written BEFORE the await so a mid-flight reload still recovers
        // the Completion screen on next mount.
        localStorage.setItem(`assessment_completed_${assessmentId}`, 'true')
        localStorage.setItem(`assessment_submitted_time_${assessmentId}`, totalTakenTime.toString())
        localStorage.setItem(`assessment_data_${assessmentId}`, JSON.stringify(assessmentData || currentAssessment))

        console.log('🔍 [Submit] Calling submitAssessment API...')
        await dispatch(submitAssessment(submissionPayload))
        console.log('✅ [Submit] Assessment submitted successfully')
        
        setSubmittedTime(totalTakenTime)
        
      } catch (submissionError) {
        console.error('❌ [Submit] Submission error:', submissionError)
        
        if (mcqQuestions.length > 0 || subjectiveQuestions.length > 0 || videoResponses.length > 0) {
          console.log('⚠️ [Submit] Submission API failed but we have answers. Showing completion anyway.')
          setSubmittedTime(totalTakenTime)
        } else {
          alert('Failed to submit assessment. Please try again.')
          completedRef.current = false
          assessmentActiveRef.current = true
          setIsCompleted(false)
          setIsAssessmentActive(true)
          return
        }
      }
      
    } catch (error) {
      console.error('❌ [Submit] Error:', error)
      completedRef.current = false
      assessmentActiveRef.current = true
      setIsCompleted(false)
      setIsAssessmentActive(true)
    }
  }, [
    questions,
    currentQuestionIndex,
    responses,
    videoBlobs,
    codingResponses,
    selectedLanguage,
    codeOutput,
    currentAssessment,
    props.assessment,
    props.currentAssessment,
    props.studentDetails,
    location.state,
    assessmentData,
    dispatch
  ])


  
useEffect(() => {
    assessmentActiveRef.current = isAssessmentActive
    completedRef.current = isCompleted
    handleSubmitRef.current = handleSubmit
  }, [isAssessmentActive, isCompleted, handleSubmit])
  // Time actually spent on the assessment, in seconds. Read from the pinned ref
  // (not state) so it is correct even inside handleSubmit's memoised closure,
  // and capped at the allotted duration so a suspended tab can't report more
  // time than the candidate was ever given.
  const calculateTotalTime = () => {
    if (assessmentStartedAtRef.current === null) {
      return 0
    }
    const elapsed = Math.floor((Date.now() - assessmentStartedAtRef.current) / 1000)
    const allotted = totalDurationRef.current
    return Math.max(0, allotted === null ? elapsed : Math.min(elapsed, allotted))
  }

  const getQuestionStatus = (questionIndex) => {
    const question = questions[questionIndex]
    if (!question) return 'unanswered'
    
    if (questionIndex === currentQuestionIndex) return 'current'
    
    const response = responses[question.id]
    if (response?.status === 'skipped') return 'skipped'
    if (response?.answer) return 'attempted'
    
    return 'unanswered'
  }

  // Calculate stats
  const attemptedCount = Object.values(responses).filter(r => r?.answer && r?.status !== 'skipped').length
  const skippedCount = Object.values(responses).filter(r => r?.status === 'skipped').length
  const remainingCount = questions.length - attemptedCount - skippedCount
  const progressPercentage = questions.length > 0 ? Math.round(((attemptedCount + skippedCount) / questions.length) * 100) : 0

const renderQuestionsBySection = () => {
  return (
    <QuestionList>
      {questions.map((question, index) => {
        const status = getQuestionStatus(index)
        
        return (
          <QuestionItem
            key={question.id}
            isExpanded={sidebarExpanded}
            disabled={!!videoRecording}
            onClick={() => handleQuestionClick(question.index)}
          >
            <QuestionNumber
              isExpanded={sidebarExpanded}
              status={status}
            >
              {question.index + 1}
            </QuestionNumber>
            {sidebarExpanded && (
              <QuestionText isExpanded={sidebarExpanded}>
                <div className="markdown-body">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {question.question_text?.substring(0, 50) + (question.question_text?.length > 50 ? '...' : '')}
                  </ReactMarkdown>
                </div>
              </QuestionText>
            )}
          </QuestionItem>
        )
      })}
    </QuestionList>
  )
}

const renderCurrentQuestion = () => {
  if (loading) {
    return <div>Loading questions...</div>
  }

  if (questions.length === 0) {
    return <div>No questions available</div>
  }

  const currentQuestion = questions[currentQuestionIndex]
  if (!currentQuestion) {
    return <div>Question not found</div>
  }

  return (
    <QuestionContainer>
      <QuestionHeader>
        <QuestionTypeTag type={currentQuestion.section_name}>
          {currentQuestion.section_name}
        </QuestionTypeTag>
      </QuestionHeader>

        {/* Main Question Display */}
        <QuestionContent>
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
            >
              {currentQuestion.question_text}
            </ReactMarkdown>
          </div>
        </QuestionContent>

        {/* MCQ Questions */}
        {currentQuestion.section_name === "MCQ Question" && (
          <div>
            <OptionsContainer>
              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                currentQuestion.options.map((option, index) => (
                  <OptionLabel
                    key={option.id}
                    checked={responses[currentQuestion.id]?.answer === option.id}
                  >
                    <RadioInput
                      type="radio"
                      name={`question_${currentQuestion.id}`}
                      value={option.id}
                      checked={responses[currentQuestion.id]?.answer === option.id}
                      onChange={() => handleMCQOptionSelect(currentQuestion.id, option.id, option.option_text)}
                    />
                    <OptionText>
                      <div className="markdown-body">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                        >
                          {option.option_text}
                        </ReactMarkdown>
                      </div>
                    </OptionText>
                  </OptionLabel>
                ))
              ) : (
                <div>No options available for this question</div>
              )}
            </OptionsContainer>
          </div>
        )}

        {/* Subjective Questions */}
        {currentQuestion.section_name === "Subjective Question" && (
          <SubjectiveContainer>
            <SubjectiveLabel>
              Provide your detailed answer:
            </SubjectiveLabel>
            <SubjectiveTextarea
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
              }}
              onPaste={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              onCopy={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              onCut={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              placeholder="Type your answer here..."
              value={subjectiveResponses[currentQuestion.id] || ''}
              onChange={(e) => handleSubjectiveResponseChange(currentQuestion.id, e.target.value)}
            />
            <CharacterCount>
              {(subjectiveResponses[currentQuestion.id] || '').split(/\s+/).filter(w => w.length > 0).length} words | {(subjectiveResponses[currentQuestion.id] || '').length} characters
              {(subjectiveResponses[currentQuestion.id] || '').split(/\s+/).filter(w => w.length > 0).length < 50 && (
                <span style={{ color: '#dc2626', marginLeft: '12px', fontWeight: '600' }}>
                  (Minimum 50 words required)
                </span>
              )}
            </CharacterCount>
          </SubjectiveContainer>
        )}

        {/* Video Response Questions */}
        {currentQuestion.section_name === "Video Response" && (
          <VideoContainer>
            {/* Delete confirmation modal */}
            {videoDeleteModalQuestion === currentQuestion.id && (
              <VideoDeleteModalOverlay onClick={handleVideoDeleteClose}>
                <VideoDeleteModalContent onClick={e => e.stopPropagation()}>
                  <VideoDeleteModalImg>
                    <img src={DeleteImage || '/placeholder.svg'} alt="delete confirmation" />
                  </VideoDeleteModalImg>
                  <VideoDeleteModalText>
                    Are you sure you want to delete this recording?
                  </VideoDeleteModalText>
                  <VideoDeleteModalButtons>
                    <button
                      onClick={handleVideoDeleteClose}
                      style={{
                        height: '38px',
                        marginRight: '10px',
                        borderRadius: '4px',
                        padding: '0 20px',
                        border: '1px solid #d1d5db',
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      No
                    </button>
                    <button
                      onClick={handleVideoConfirmDelete}
                      style={{
                        height: '38px',
                        borderRadius: '4px',
                        padding: '0 20px',
                        background: '#4318FF',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      Yes
                    </button>
                  </VideoDeleteModalButtons>
                </VideoDeleteModalContent>
              </VideoDeleteModalOverlay>
            )}

            {videoShowSuccessModal && (
              <SuccessModal
                open={videoShowSuccessModal}
                onCloseClick={handleVideoSuccessModalClose}
                message="Recording deleted successfully!"
                title="Delete Recording"
              />
            )}

            <VideoInfoContainer>
              <VideoInfoIcon>i</VideoInfoIcon>
              <VideoInfoText>
                Record your response to the question and make sure{' '}
                <strong>it's at least 1 minute long and at most 2 minutes.</strong>
              </VideoInfoText>
            </VideoInfoContainer>

            {/* Live recording view */}
            {videoRecording === currentQuestion.id && videoStreams[currentQuestion.id] && (
              <VideoRecordingArea>
                <VideoPlayer>
                  <video
                    ref={(el) => {
                      if (el && !el.srcObject && videoStreams[currentQuestion.id]) {
                        try {
                          el.srcObject = videoStreams[currentQuestion.id]
                          el.play().catch(error => {
                            console.error('Error playing live preview:', error)
                          })
                        } catch (error) {
                          console.error('Error setting srcObject:', error)
                        }
                      }
                    }}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '12px',
                      display: 'block',
                    }}
                  />
                  <VideoTimerBadge>
                    <VideoTimerDot />
                    {formatVideoTime(videoRecordingTimer)}
                  </VideoTimerBadge>
                </VideoPlayer>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', width: '100%' }}>
                  <VideoStopButton
                    onClick={videoMinReached ? () => stopVideoRecording(currentQuestion.id) : undefined}
                    enabled={videoMinReached}
                  >
                    Stop recording
                    <StopRecordIcon />
                  </VideoStopButton>
                </div>
              </VideoRecordingArea>
            )}

            {/* Recorded video playback */}
            {recordedVideos[currentQuestion.id] && videoRecording !== currentQuestion.id && (
              <VideoRecordingArea>
                <VideoPlayer style={{ aspectRatio: 'unset' }}>
                  <video
                    src={recordedVideos[currentQuestion.id]}
                    controls
                    playsInline
                    webkit-playsinline="true"
                    style={{
                      width: '100%',
                      borderRadius: '12px',
                      display: 'block',
                      height: 'auto',
                    }}
                  />
                </VideoPlayer>

                {getVideoAttempts(currentQuestion.id) > 0 && (
                  <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <VideoReRecordButton onClick={() => handleVideoDeleteClick(currentQuestion.id)}>
                      Re-record
                      <Retry style={{ marginLeft: '8px' }} />
                    </VideoReRecordButton>
                    <VideoAttemptsText>
                      You have{' '}
                      <strong style={{ fontWeight: '800' }}>
                        {getVideoAttempts(currentQuestion.id)}{' '}
                        {getVideoAttempts(currentQuestion.id) === 1 ? 'chance' : 'chances'}
                      </strong>{' '}
                      left
                    </VideoAttemptsText>
                  </div>
                )}
              </VideoRecordingArea>
            )}

            {/* Initial record prompt */}
            {!recordedVideos[currentQuestion.id] && videoRecording !== currentQuestion.id && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px', marginBottom: '30px' }}>
                <VideoRecordButton
                  onClick={() => startVideoRecording(currentQuestion.id)}
                  disabled={getVideoAttempts(currentQuestion.id) === 0}
                >
                  Record
                  <WhiteRecordIcon />
                </VideoRecordButton>
                <VideoAttemptsText>
                  You have{' '}
                  <strong style={{ fontWeight: '800' }}>
                    {getVideoAttempts(currentQuestion.id)}{' '}
                    {getVideoAttempts(currentQuestion.id) === 1 ? 'chance' : 'chances'}
                  </strong>{' '}
                  left
                </VideoAttemptsText>
              </div>
            )}
          </VideoContainer>
        )}

        {/* Coding Questions */}
        {currentQuestion.section_name === "Coding Question" && (
          <div style={{ marginTop: '16px' }}>
            {/* Problem details from codingMetadata */}
            {currentQuestion.coding_metadata && (
              <div style={{ marginBottom: '16px' }}>
                {currentQuestion.coding_metadata.input_format && (
                  <div style={{ margin: '8px 0' }}>
                    <strong style={{ fontSize: '13px', color: '#475569' }}>Input Format:</strong>
                    <pre style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', fontSize: '13px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                      {currentQuestion.coding_metadata.input_format}
                    </pre>
                  </div>
                )}
                {currentQuestion.coding_metadata.output_format && (
                  <div style={{ margin: '8px 0' }}>
                    <strong style={{ fontSize: '13px', color: '#475569' }}>Output Format:</strong>
                    <pre style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', fontSize: '13px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                      {currentQuestion.coding_metadata.output_format}
                    </pre>
                  </div>
                )}
                {currentQuestion.coding_metadata.constraints && (
                  <div style={{ margin: '8px 0' }}>
                    <strong style={{ fontSize: '13px', color: '#475569' }}>Constraints:</strong>
                    <pre style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', fontSize: '13px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                      {currentQuestion.coding_metadata.constraints}
                    </pre>
                  </div>
                )}
                {currentQuestion.coding_metadata.examples && currentQuestion.coding_metadata.examples.length > 0 && (
                  <div style={{ margin: '12px 0' }}>
                    <strong style={{ fontSize: '13px', color: '#475569' }}>Examples:</strong>
                    {currentQuestion.coding_metadata.examples.map((ex, idx) => (
                      <div key={idx} style={{ background: '#f1f5f9', padding: '10px', borderRadius: '6px', marginTop: '6px', fontSize: '13px' }}>
                        <div><strong>Input:</strong> <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '3px' }}>{ex.input}</code></div>
                        <div style={{ marginTop: '4px' }}><strong>Output:</strong> <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '3px' }}>{ex.output}</code></div>
                        {ex.explanation && <div style={{ color: '#64748b', marginTop: '4px', fontStyle: 'italic' }}>{ex.explanation}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Language selector — uses question's languages, filtered to those
                the backend can actually score. Falls back to python/javascript
                so we never silently submit answers in a language that scores 0. */}
            {(() => {
              const EVALUATED = ['python', 'javascript', 'typescript', 'java', 'cpp', 'c']
              const declared = (currentQuestion.coding_metadata?.languages || []).filter(l => EVALUATED.includes(l))
              const availableLangs = declared.length > 0 ? declared : ['python', 'javascript']
              return (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Language:</label>
              <select
                value={selectedLanguage[currentQuestion.id] || availableLangs[0]}
                onChange={(e) => handleLanguageChange(currentQuestion.id, e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                {availableLangs.map(lang => (
                  <option key={lang} value={lang}>
                    {lang === 'cpp' ? 'C++' : lang === 'javascript' ? 'JavaScript' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </option>
                ))}
              </select>
            </div>
              )
            })()}

            {/* Code editor textarea */}
            <textarea
              placeholder="Write your code here..."
              value={codingResponses[currentQuestion.id] || ''}
              onChange={(e) => handleCodingResponseChange(currentQuestion.id, e.target.value)}
              onBlur={() => saveCodingResponse(currentQuestion.id)}
              onPaste={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              onCopy={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              onCut={disableCopyPaste ? (e) => e.preventDefault() : undefined}
              onKeyDown={(e) => {
                // Handle Tab key for indentation
                if (e.key === 'Tab') {
                  e.preventDefault()
                  const start = e.target.selectionStart
                  const end = e.target.selectionEnd
                  const value = e.target.value
                  const newValue = value.substring(0, start) + '  ' + value.substring(end)
                  handleCodingResponseChange(currentQuestion.id, newValue)
                  setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = start + 2
                  }, 0)
                }
              }}
              style={{
                width: '100%',
                minHeight: '300px',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontFamily: '"Fira Code", "Courier New", monospace',
                fontSize: '14px',
                lineHeight: '1.6',
                resize: 'vertical',
                background: '#1e293b',
                color: '#e2e8f0',
                tabSize: 2,
              }}
            />
            {/* Action row — kept directly under the textarea so clicking the
                Run Code button is stable. The save-status banner is rendered
                AFTER this row (below), so the button doesn't shift when the
                banner appears mid-click (textarea.onBlur fires before mouseup,
                a layout shift here would land the click on empty space). */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {(codingResponses[currentQuestion.id] || '').split('\n').length} lines
                {codingResponses[currentQuestion.id]?.trim() ? ' | Code entered' : ' | No code yet'}
              </div>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => runCode(currentQuestion.id)}
                disabled={codeRunning[currentQuestion.id] || !codingResponses[currentQuestion.id]?.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  background: codeRunning[currentQuestion.id] ? '#6b7280' : '#059669',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: codeRunning[currentQuestion.id] ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {codeRunning[currentQuestion.id] ? (
                  <>Running...</>
                ) : (
                  <>&#9654; Run Code</>
                )}
              </button>
            </div>
            {(() => {
              const s = codingSaveStatus[currentQuestion.id]
              if (!s) return null
              const palette = s.state === 'error'
                ? { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' }
                : s.state === 'saving'
                  ? { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af' }
                  : { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46' }
              const label = s.state === 'error'
                ? (s.message || 'Save failed.')
                : s.state === 'saving' ? 'Saving…' : 'Saved'
              return (
                <div
                  role={s.state === 'error' ? 'alert' : 'status'}
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background: palette.bg,
                    border: `1px solid ${palette.border}`,
                    color: palette.fg,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span>{label}</span>
                  {s.state === 'error' && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => saveCodingResponse(currentQuestion.id)}
                      style={{
                        marginLeft: 'auto',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        border: '1px solid #fca5a5',
                        background: '#fff',
                        color: '#991b1b',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )
            })()}

            {/* Code output panel */}
            {codeOutput[currentQuestion.id] && (
              <div style={{
                marginTop: '12px',
                borderRadius: '8px',
                border: `1px solid ${codeOutput[currentQuestion.id].status === 'passed' ? '#bbf7d0' : codeOutput[currentQuestion.id].status === 'error' ? '#fecaca' : codeOutput[currentQuestion.id].status === 'partial' ? '#fde68a' : '#e2e8f0'}`,
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: codeOutput[currentQuestion.id].status === 'passed' ? '#f0fdf4' :
                              codeOutput[currentQuestion.id].status === 'error' ? '#fef2f2' :
                              codeOutput[currentQuestion.id].status === 'partial' ? '#fffbeb' :
                              codeOutput[currentQuestion.id].status === 'running' ? '#eff6ff' : '#f8fafc',
                  color: codeOutput[currentQuestion.id].status === 'passed' ? '#166534' :
                         codeOutput[currentQuestion.id].status === 'error' ? '#dc2626' :
                         codeOutput[currentQuestion.id].status === 'partial' ? '#92400e' :
                         codeOutput[currentQuestion.id].status === 'running' ? '#1d4ed8' : '#374151',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>
                    {codeOutput[currentQuestion.id].status === 'passed' ? 'All Tests Passed' :
                     codeOutput[currentQuestion.id].status === 'error' ? 'Error' :
                     codeOutput[currentQuestion.id].status === 'partial' ? 'Some Tests Failed' :
                     codeOutput[currentQuestion.id].status === 'running' ? 'Running...' : 'Output'}
                  </span>
                  {codeOutput[currentQuestion.id].totalCount > 0 && (
                    <span style={{ fontWeight: '400' }}>
                      {codeOutput[currentQuestion.id].passedCount}/{codeOutput[currentQuestion.id].totalCount} passed
                    </span>
                  )}
                </div>
                <div style={{
                  padding: '8px 12px',
                  background: '#f8fafc',
                  fontSize: '13px',
                  color: '#374151',
                }}>
                  {codeOutput[currentQuestion.id].output}
                </div>
                {/* Individual test case results */}
                {codeOutput[currentQuestion.id].testResults && codeOutput[currentQuestion.id].testResults.map((result, idx) => (
                  <div key={idx} style={{
                    borderTop: '1px solid #e2e8f0',
                    padding: '10px 12px',
                    background: result.passed ? '#f0fdf4' : result.isError ? '#fef2f2' : '#fffbeb',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                    }}>
                      <span style={{
                        fontWeight: '600',
                        fontSize: '12px',
                        color: result.passed ? '#166534' : '#dc2626',
                      }}>
                        {result.passed ? '\u2713' : '\u2717'} {result.label}
                      </span>
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        background: result.passed ? '#bbf7d0' : '#fecaca',
                        color: result.passed ? '#166534' : '#991b1b',
                        fontWeight: '500',
                      }}>
                        {result.passed ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                    {result.is_hidden ? (
                      <div style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                        Hidden test case
                      </div>
                    ) : (
                      <>
                        {result.input && (
                          <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Input:</span>{' '}
                            <code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>{result.input}</code>
                          </div>
                        )}
                        {result.expected && (
                          <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                            <span style={{ color: '#6b7280', fontWeight: '500' }}>Expected:</span>{' '}
                            <code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>{result.expected}</code>
                          </div>
                        )}
                        <div style={{ fontSize: '11px' }}>
                          <span style={{ color: '#6b7280', fontWeight: '500' }}>Actual:</span>{' '}
                          <code style={{
                            background: result.isError ? '#fecaca' : '#e2e8f0',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            color: result.isError ? '#991b1b' : 'inherit',
                          }}>{result.actual}</code>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
    </QuestionContainer>
  )
}

  // Check for completion recovery — scoped to THIS assessment_assigned_id only.
  // Prevents a stale flag from a different (or previous) assessment from
  // forcing the user straight to the Thank You page on a fresh attempt.
  useEffect(() => {
    const recoveryAssessmentId =
      currentAssessment?.assessment_assigned_id ||
      props.assessment?.assessment_assigned_id ||
      props.currentAssessment?.assessment_assigned_id ||
      location?.state?.assessmentData?.assessment_assigned_id ||
      location?.state?.assessment_assigned_id ||
      assessmentData?.assessment_assigned_id
    if (!recoveryAssessmentId) return

    const completedKey = `assessment_completed_${recoveryAssessmentId}`
    const timeKey = `assessment_submitted_time_${recoveryAssessmentId}`
    const dataKey = `assessment_data_${recoveryAssessmentId}`

    const wasCompleted = localStorage.getItem(completedKey)
    if (wasCompleted === 'true') {
      const submittedTime = parseInt(localStorage.getItem(timeKey) || '0')
      const savedAssessmentData = JSON.parse(localStorage.getItem(dataKey) || '{}')

      setSubmittedTime(submittedTime)
      setIsCompleted(true)

      localStorage.removeItem(completedKey)
      localStorage.removeItem(timeKey)
      localStorage.removeItem(dataKey)
    }
  }, [])

  // Main render with modal priority handling
  // Show completion screen BEFORE re-entry modal (this is the key fix)
  if (isCompleted) {
    return (
      <Completion 
        assessmentData={assessmentData || currentAssessment} 
        submittedTime={submittedTime}
        setAssessment={props.setAssessment}
      />
    )
  }
  // Show fullscreen entry modal first (before assessment starts) - only when fullscreen lockdown is enforced
  if (enforceFullscreen && showFullscreenModal && !isCompleted) {
    return <FullscreenEntryModal onAccept={requestFullscreenAccess} />
  }

  // Show re-entry modal if user exited fullscreen / switched away (only when proctoring enabled and not completed)
  if (proctoringEnabled && showReentryModal && !isCompleted) {
    return (
      <FullscreenReentryModal
        onReturn={enforceFullscreen ? handleReturnToFullscreen : handleReturnLite}
        warningCount={tabSwitchCount}
        lite={!enforceFullscreen}
      />
    )
  }

  // Show violation modal if user exceeded warnings (only when proctoring enabled and not completed)
  if (proctoringEnabled && showViolationModal && !isCompleted) {
    return <ViolationModal onSubmit={submitAfterViolation} />
  }

  // Don't render assessment until it's active (only gate when proctoring enabled)
  if (proctoringEnabled && !isAssessmentActive) {
    return null
  }
  // Main assessment render
  return (
    <>
      <MarkdownGlobalStyle />
      <FullscreenAssessmentContainer className="exam-shell" isFullscreen={isAssessmentFullscreen}>
        <SidebarNav isExpanded={sidebarExpanded}>
          <SidebarHeader isExpanded={sidebarExpanded}>
            <SidebarTitle isExpanded={sidebarExpanded}>
              Questions ({questions.length})
            </SidebarTitle>
            <ToggleButton onClick={toggleSidebar}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={sidebarExpanded ? 'M18 6L6 18M6 6l12 12' : 'M9 18l6-6-6-6'}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ToggleButton>
          </SidebarHeader>
          
          <SidebarContent isExpanded={sidebarExpanded}>
            {renderQuestionsBySection()}
          </SidebarContent>
        </SidebarNav>

        <ContentArea className="exam-scroll">
          <AssessmentContent>
            <AssessmentHeader>
              <AssessmentInfo>
                <LogoContainer>
                  <PluginLiveLogo />
                </LogoContainer>
                <VerticalSeparator />
                <AssessmentTitle>Role-Based Assessment</AssessmentTitle>
              </AssessmentInfo>
              <AssessmentTimer>
                {formatTime(timeLeft)}
              </AssessmentTimer>
            </AssessmentHeader>

            <AssessmentSubtitle>
              <SubtitleText>
                Question {currentQuestionIndex + 1} of {questions.length}
                {questions[currentQuestionIndex] && ` • ${questions[currentQuestionIndex].section_name}`}
              </SubtitleText>
            </AssessmentSubtitle>

            {/* Status section */}
            <StatusSection>
              <ProgressContainer>
                <ProgressText>
                  Progress: {attemptedCount + skippedCount} of {questions.length} ({progressPercentage}%)
                </ProgressText>
                <ProgressBar progress={progressPercentage} />
              </ProgressContainer>

              <StatusItem>
                <StatusNumber color="#10b981">{attemptedCount}</StatusNumber>
                <StatusLabel>Answered</StatusLabel>
              </StatusItem>

              <StatusItem>
                <StatusNumber color="#6b7280">{remainingCount}</StatusNumber>
                <StatusLabel>Remaining</StatusLabel>
              </StatusItem>

              <StatusSeparator />

              <StatusItem>
                <StatusNumber color="#9ca3af">{skippedCount}</StatusNumber>
                <StatusLabel>Skipped</StatusLabel>
              </StatusItem>
            </StatusSection>

            {renderCurrentQuestion()}
          </AssessmentContent>

<StickyNavigationContainer className="exam-actions">
  <ButtonContainer className="exam-actions">
    <NavigationButton
      variant="secondary"
      onClick={handlePrevious}
      disabled={currentQuestionIndex === 0 || videoRecording}
    >
      Previous
    </NavigationButton>
    <NavigationButton
      variant="secondary"
      onClick={handleSkip}
      disabled={videoRecording}
    >
      Skip
    </NavigationButton>
  </ButtonContainer>
  
  <ButtonContainer className="exam-actions">
    {(() => {
      const activeQuestion = questions[currentQuestionIndex]
      const isVideoQuestion = activeQuestion?.section_name === 'Video Response'
      const videoNotRecorded = isVideoQuestion && !recordedVideos[activeQuestion.id]
      const isLastQuestion = currentQuestionIndex === questions.length - 1
      return isLastQuestion ? (
        <NavigationButton
          variant="danger"
          onClick={handleSubmit}
          disabled={!!videoRecording || videoNotRecorded}
          title={videoNotRecorded ? 'Record your video response to continue' : undefined}
        >
          {videoRecording
            ? 'Recording in Progress...'
            : videoNotRecorded
              ? 'Record video to submit'
              : 'Submit Assessment'}
        </NavigationButton>
      ) : (
        <NavigationButton
          variant="primary"
          onClick={handleNext}
          disabled={!!videoRecording || videoNotRecorded}
          title={videoNotRecorded ? 'Record your video response to continue' : undefined}
        >
          {videoRecording
            ? 'Recording...'
            : videoNotRecorded
              ? 'Record video to continue'
              : 'Next'}
        </NavigationButton>
      )
    })()}
  </ButtonContainer>
</StickyNavigationContainer>
        </ContentArea>
      </FullscreenAssessmentContainer>
    </>
  )
}

export default RoleBasedAssessment
